import { debounce } from 'lodash';
import { CRDTServiceWS, crdtServiceWS } from 'services/crdt/crdtServiceWS';
import { WebsocketProvider } from 'y-websocket';
import * as Y from 'yjs';
import { DOMSerializer, DOMParser as PMDOMParser } from 'prosemirror-model';
import { createFileLogger, formatJSON } from '../../../../utils/logger';
import { entityService } from '../../../../services/entityService';
import { userService } from '../../../../services/users/userService';
import { initProseMirrorDoc } from 'y-prosemirror';
// CRITICAL: Import LibraryApp plugin BEFORE schema to ensure citation mark is registered
import '../../../LibraryApp/integration/initializeLibraryPlugin';
import { schema as baseSchema } from '../../editor/schema';
import { schemaExtensionRegistry } from '../../plugins/schemaExtensionRegistry';
import { XmlFragment } from '../../../../shared/yjs';
import { createYElement } from '../doc/internal/yDocLowLevelAPI';
import { replaceWithMarkdown as docReplaceWithMarkdown } from '../doc/docApi';
import { IS_DEV } from '../../../../utils/devMode';
import { parseJwtPayload } from '../../../../utils/jwtUtils';
import useSpaceStore from '../../../../stores/spaceStore';



const log = createFileLogger('DOCSCRDTSERVICE_APP', { enabled: false });

/** Server is durability boundary; omit base64 from client merge payloads */
const SERVER_IS_DURABILITY_BOUNDARY = true;

// Build extended schema with all plugin marks/nodes
const schema = schemaExtensionRegistry.getRegisteredMarks().length > 0 || schemaExtensionRegistry.getRegisteredNodes().length > 0
  ? schemaExtensionRegistry.buildExtendedSchema(baseSchema)
  : baseSchema;

log.log('[docsCRDTService] Using schema with marks:', {
  marks: Object.keys(schema.marks),
  hasCitation: !!schema.marks.citation
});


// 🔥 PERFORMANCE: Only log debug info in development
const debugLog = IS_DEV ? log.log.bind(log) : () => {};

// Welcome image from public folder
const WELCOME_IMAGE_URL = '/welcomeDocImg.png';


interface CRDTUpdate<T> {
  data: T;
  type: string;
  timestamp: number;
}

interface DocUpdate {
  content: string;
}

// Global initialization lock to prevent Y.js corruption
const globalInitializationLock = new Map<string, Promise<any>>();

export class DocsCRDTService {
  private yContentCache = new Map<string, Y.XmlFragment>();
  private activeDocObservers = new Map<string, () => void>();
  private saveSuppressionUntil = new Map<string, number>();
  private initializationPromises = new Map<string, Promise<{doc: Y.Doc, provider?: WebsocketProvider}>>();
  private currentDocId: string | null = null;
  
  // ✅ CRITICAL: Cache entity types for correct WebSocket room naming
  // Note entities use 'note:' prefix, Document entities use 'doc:' prefix
  private entityTypes = new Map<string, string>();
  
  // ✅ CRITICAL: Prevent infinite loop when getTextContent() triggers Y.js transactions
  // Use Set<any> to store fragment object references as keys
  private isInTextExtraction = new Set<any>();
  
  // ✅ Track previous document lengths for deletion detection
  private previousLengths = new Map<string, number>();
  
  // 🛡️ FLOOD DETECTION: Monitor connection patterns to detect bugs
  private connectionAttempts = new Map<string, number>();
  private recentConnectionTimestamps: number[] = [];
  private readonly CONNECTION_WARNING_THRESHOLD = 3;
  private readonly FLOOD_DETECTION_WINDOW_MS = 5000; // 5 seconds
  private readonly FLOOD_THRESHOLD = 20; // 20 connections in 5 seconds = potential flood

  // 🛡️ CORRUPTION TRACKING: Track documents with corrupted base64 to prevent data loss
  private corruptedDocuments = new Set<string>();
  
  // Track consecutive WS connection failures per doc for REST recovery
  private wsFailureCounts = new Map<string, number>();
  private wsRecoveryInFlight = new Set<string>();
  private readonly WS_FAILURE_RECOVERY_THRESHOLD = 3;
  
  // 🔧 FIX: Per-document debounced save functions to prevent argument clobbering
  // When a single debounce is shared, multiple docs within 500ms cause only the LAST one to save!
  private perDocumentDebouncedSaves = new Map<string, ReturnType<typeof debounce>>();
  
  // Prevent spam saves on frequent reconnections
  private lastReconnectSaveTime = new Map<string, number>();
  
  // ✅ SMART CACHE VERSION TRACKING: Track document versions for deterministic stale detection
  // Maps docId → version number
  // 
  // HOW IT WORKS:
  // - Backend increments version on every save (atomic $inc)
  // - Frontend tracks version in memory and compares with backend
  // - If backend version > cached version → recreate Y.Doc from backend (stale cache)
  // - If versions match → reuse cached Y.Doc (fast path)
  // 
  // BACKWARDS COMPATIBILITY (No migration needed!):
  // - Old docs without version field: implicitly version 0
  // - First save with new code: MongoDB $inc creates field and sets to 1
  // - System self-heals: old docs get version field on first save
  // - Missing version = 0: won't trigger false stale detection (backendVersion > 0 check)
  private docVersions = new Map<string, number>();
  
  // ⚡ PERFORMANCE: Version check cache with TTL (reduces API calls)
  private versionCache = new Map<string, { version: number; timestamp: number }>();
  private readonly VERSION_CACHE_TTL = 5000; // 5 seconds
  
  // Track expected document sizes from backend to detect WebSocket content loss
  private expectedDocumentSizes = new Map<string, number>();
  
  // Track last local edit time to prevent recovery from overwriting user's work
  private lastLocalEditTime = new Map<string, number>();
  
  // ✅ STALE VERSION RECREATION TRACKING: Track when we recreate due to stale version
  // Used to detect if WebSocket sync overwrites freshly loaded backend content
  // Maps docId → { backendVersion, cachedVersion, timestamp, expectedElementCount? }
  private recreatingFromStaleVersion = new Map<string, {
    backendVersion: number;
    cachedVersion: number;
    timestamp: number;
    expectedElementCount?: number;
    initialBase64?: string;
  }>();
  
  // 📊 PERFORMANCE MONITORING: Track lock acquisition times
  private lockMetrics = new Map<string, number>();

  // Reference counting for editor-held Y.Docs. When no editors hold a doc,
  // schedule a full disconnect (Y.Doc + WebsocketProvider + IndexedDB) after a
  // short grace period. The grace handles React StrictMode remounts and rapid
  // doc-switch navigations without tearing down and re-opening the socket.
  private refCounts = new Map<string, number>();
  private pendingReleases = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly RELEASE_GRACE_MS = 5000;

  constructor(private crdt: CRDTServiceWS) {
    if (typeof window !== 'undefined') {
      // Flush all pending saves when the tab becomes hidden. This covers tab-switch,
      // window minimise, and most tab-close scenarios — and fires *before* the page
      // starts tearing down, so async HTTP requests can still complete normally.
      const visibilityHandler = () => {
        if (document.visibilityState === 'hidden') {
          for (const [, debouncedFn] of this.perDocumentDebouncedSaves.entries()) {
            try {
              debouncedFn.flush();
            } catch (e) {
              // Non-fatal — WS server will persist within 3s of the last edit
            }
          }
        }
      };
      document.addEventListener('visibilitychange', visibilityHandler);

      // Also flush on beforeunload as a last-resort safety net (async requests
      // may be aborted by the browser, but visibilitychange above handles most cases).
      window.addEventListener('beforeunload', () => {
        for (const [, debouncedFn] of this.perDocumentDebouncedSaves.entries()) {
          try {
            debouncedFn.flush();
          } catch (e) {
            // Ignore errors during unload
          }
        }
      });
    }
  }

  /**
   * Helper: Execute a promise with timeout protection
   */
  private async withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    operation: string
  ): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) =>
        setTimeout(
          () => reject(new Error(`${operation} timeout after ${timeoutMs}ms`)),
          timeoutMs
        )
      )
    ]);
  }

  // Set up user awareness for collaborative cursors
  private async setupUserAwareness(provider?: WebsocketProvider, docId?: string, doc?: Y.Doc) {
    if (!provider?.awareness) {
      log.warn('[DocsApp - CRDT] No awareness object available on provider');
      return;
    }

    try {
      // Idempotence and cleanup of previous setup if any
      const anyProvider = provider as any;
      try {
        if (anyProvider.__awarenessCleanup) {
          anyProvider.__awarenessCleanup();
        }
      } catch {}
      if (anyProvider.__awarenessSetupActive) {
        // Already set up; avoid stacking listeners/handlers
        return;
      }

      // Get current user information from multiple sources
      let userId = 'anonymous';
      let userName = 'Anonymous User';
      let userEmail = '';
      
      // Method 1: Try to get user info from Auth0 context (most reliable)
      try {
        const { user: auth0User } = (window as any).__AUTH0_CONTEXT__ || {};
        if (auth0User) {
          userId = auth0User.sub || 'anonymous';
          userName = auth0User.name || auth0User.email || 'Anonymous User';
          userEmail = auth0User.email || '';
          debugLog('[DocsApp - CRDT] Got user from Auth0 context:', { userId: userId.slice(-8), userName });
        }
      } catch (auth0Error) {
        log.warn('[DocsApp - CRDT] Could not access Auth0 context:', auth0Error);
      }
      
      // Method 2: Try legacy window.__auth0User
      if (userId === 'anonymous') {
        const auth0User = (window as any).__auth0User;
        if (auth0User) {
          userId = auth0User.sub || 'anonymous';
          userName = auth0User.name || auth0User.email || 'Anonymous User';
          userEmail = auth0User.email || '';
          debugLog('[DocsApp - CRDT] Got user from window.__auth0User:', { userId: userId.slice(-8), userName });
        }
      }
      
      // Method 3: Try to get user info from localStorage token
      if (userId === 'anonymous') {
        const token = localStorage.getItem('authToken');
        if (token) {
          try {
            const payload = parseJwtPayload(token);
            if (payload) {
              userId = payload.sub || 'anonymous';
              userName = payload.name || payload.email || 'Anonymous User';
              userEmail = payload.email || '';
            }
            debugLog('[DocsApp - CRDT] Got user from localStorage token:', { userId: userId.slice(-8), userName });
          } catch (error) {
            log.error('[DocsApp - CRDT] Error parsing auth token for awareness:', error);
          }
        }
      }
      
      // 🔥 Method 4: Fetch user profile to get real name/email (JWT often missing these)
      if (userId !== 'anonymous') {
        try {
          const userProfile = await userService.getUserProfile();
          if (userProfile?.skeleton) {
            const profileName = userProfile.skeleton.name || 
                              userProfile.skeleton.auth0?.name || 
                              userProfile.skeleton.auth0?.nickname;
            const profileEmail = userProfile.skeleton.email || 
                               userProfile.skeleton.auth0?.email;
            
            // Use profile name if available, otherwise use email as fallback
            if (profileName) {
              userName = profileName;
            } else if (profileEmail) {
              userName = profileEmail;  // 🔥 Email as name fallback
            }
            
            if (profileEmail) {
              userEmail = profileEmail;
            }
          }
        } catch (profileError) {
          // Continue with token data if profile fetch fails
          log.warn('[DocsApp - CRDT] Could not fetch user profile for awareness:', profileError);
        }
      }
      
      // Generate a consistent color for the user based on their ID
      const getUserColor = (id: string) => {
        const colors = [
          '#3b82f6', '#ef4444', '#10b981', '#f59e0b', 
          '#8b5cf6', '#06b6d4', '#84cc16', '#f97316',
          '#ec4899', '#6366f1', '#14b8a6', '#f472b6'
        ];
        const hash = id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
      };
      
      // Set the local awareness state for this user
      const userAwarenessState = {
        user: {
          id: userId,
          name: userName,
          email: userEmail,
          color: getUserColor(userId)
        }
      };
      
      provider.awareness.setLocalState(userAwarenessState);
      
      // Verify the awareness state was set correctly
      const verifyState = provider.awareness.getLocalState();
      debugLog('[DocsApp - CRDT] User awareness state set and verified:', {
        userId: userId.slice(-8), // Show last 8 chars for privacy
        userName,
        userEmail,
        color: getUserColor(userId),
        awarenessSetSuccessfully: !!verifyState?.user,
        verifiedUser: verifyState?.user,
        totalAwarenessStates: provider.awareness.getStates().size,
        providerConnected: provider.wsconnected,
        providerSynced: provider.synced,
        roomName: provider.roomname,
        websocketUrl: provider.url
      });
      
      // Add awareness change listener to debug multiplayer connectivity
      const awarenessChangeHandler = ({ added, updated, removed }: { added: number[], updated: number[], removed: number[] }) => {
        const totalStates = provider.awareness.getStates().size;
        
        // Reduce noisy console logs; only surface when visible and not our own awareness
        try {
          const localClientId = (provider.awareness as any).clientID;
          const selfLeft = removed.includes(localClientId);
          if (added.length > 0 && totalStates > 1 && document.visibilityState === 'visible') {
            log.log('[DocsApp - CRDT] Multiplayer detected (another user joined)');
          }
          if (removed.length > 0 && document.visibilityState === 'visible' && !selfLeft) {
            log.log('[DocsApp - CRDT] A remote user left the document');
          }
        } catch {}
      };
      
      provider.awareness.on('change', awarenessChangeHandler);
      // Also add a WebSocket message listener to see if messages are being received
      const originalOnMessage = provider.ws?.onmessage;
      if (provider.ws) {
        provider.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'awareness') {
              debugLog('[DocsApp - CRDT] 📡 Received awareness message from server:', {
                type: data.type,
                clientsInMessage: Object.keys(data.awareness || {}).length,
                roomName: provider.roomname
              });
            }
          } catch (e) {
            // Not JSON or not awareness message
          }
          
          // Call original handler with error handling for corrupted Yjs messages
          if (originalOnMessage && provider.ws) {
            try {
              originalOnMessage.call(provider.ws, event);
            } catch (decodeError) {
              // Gracefully handle corrupted WebSocket messages (e.g., "Unexpected end of array")
              // This can happen when Yjs binary messages are incomplete/corrupted
              console.warn('[DocsApp - CRDT] ⚠️ WebSocket message decode error (non-fatal):', {
                error: decodeError instanceof Error ? decodeError.message : String(decodeError),
                roomName: provider.roomname
              });
              // Don't crash - Yjs will re-sync on next update
            }
          }
        };
        // Track wrapper/original for proper cleanup
        anyProvider.__originalWsOnMessage = originalOnMessage || null;
        anyProvider.__wsOnMessageWrapped = true;
      }
      
      // Add periodic awareness heartbeat to keep connection alive
      const awarenessHeartbeat = setInterval(() => {
        // Only send heartbeat if tab is visible and we have a valid user
        if (document.visibilityState === 'visible' && userId !== 'anonymous') {
          const currentLocal = provider.awareness.getLocalState();
          if (!currentLocal?.user) {
            debugLog('[DocsApp - CRDT] 💓 Heartbeat: Restoring lost awareness state');
            provider.awareness.setLocalState(userAwarenessState);
          }
        }
      }, 10000); // Check every 10 seconds
      
      // Store cleanup functions for this provider
      const cleanupFunctions = () => {
        // REMOVED: Event listener cleanup (listeners were removed to fix spam)
        provider.awareness.off('change', awarenessChangeHandler);
        clearInterval(awarenessHeartbeat);
        try {
          if (provider.ws && anyProvider.__wsOnMessageWrapped) {
            provider.ws.onmessage = anyProvider.__originalWsOnMessage || null;
          }
        } catch {}
        anyProvider.__wsOnMessageWrapped = false;
        anyProvider.__awarenessSetupActive = false;
      };
      
      // Store cleanup function on the provider for later cleanup
      anyProvider.__awarenessCleanup = cleanupFunctions;
      anyProvider.__awarenessSetupActive = true;
      
      // Method 4: Try userStore as fallback and update awareness if better data becomes available
      if (userId === 'anonymous') {
        import('../../../../stores/userStore').then(({ default: useUserStore }) => {
          const userState = useUserStore.getState();
          if (userState.auth0Id) {
            const storeUserId = userState.auth0Id;
            const storeUserName = userState.profile?.skeleton?.name || userState.profile?.skeleton?.auth0?.name || 'User';
            const storeUserEmail = userState.profile?.skeleton?.auth0?.email || '';
            
            // Update awareness with userStore data
            provider.awareness.setLocalState({
              user: {
                id: storeUserId,
                name: storeUserName,
                email: storeUserEmail,
                color: getUserColor(storeUserId)
              }
            });
            
            debugLog('[DocsApp - CRDT] Updated awareness with userStore data:', { 
              userId: storeUserId.slice(-8), 
              userName: storeUserName 
            });
          }
        }).catch(storeError => {
          log.warn('[DocsApp - CRDT] Could not access userStore:', storeError);
        });
      }
      
    } catch (error) {
      log.error('[DocsApp - CRDT] Error setting user awareness state:', error);
    }
  }

  /**
   * Get cached Y.Doc if already initialized, without triggering initialization
   * Used by contextStore to avoid double initialization
   * Returns the promise if document is being/has been initialized, null otherwise
   */
  getCachedYDoc(docId: string): Promise<{ doc: Y.Doc; provider?: WebsocketProvider }> | null {
    return this.initializationPromises.get(docId) || null;
  }

  async getYDocForDoc(
    docId: string, 
    initialContent?: string, 
    initialBase64?: string, 
    initialName?: string, 
    includeWelcomeImage?: boolean,
    pageMargins?: { top: string; right: string; bottom: string; left: string },
    knownEntityType?: string
  ): Promise<{doc: Y.Doc, provider?: WebsocketProvider}> {
    const crdtStartTime = Date.now();
    const crdtTraceId = `${docId.slice(-8)}_${Date.now()}`;
    
    // Track current document
    this.currentDocId = docId;
    
    log.log(`🔵 [CRDT_TRACE:${crdtTraceId}] === CRDT.GETYDO CFORDOC START ===`, {
      docId: docId.slice(-8),
      hasBase64: !!initialBase64,
      base64Length: initialBase64?.length || 0,
      hasContent: !!initialContent,
      contentLength: typeof initialContent === 'string' ? initialContent.length : 0,
      name: initialName || 'unnamed',
      includeWelcomeImage,
      timestamp: new Date().toISOString(),
      traceId: crdtTraceId
    });
    
    // 🛡️ FLOOD DETECTION: Monitor connection patterns (non-blocking)
    const now = Date.now();
    this.recentConnectionTimestamps.push(now);
    this.recentConnectionTimestamps = this.recentConnectionTimestamps.filter(
      ts => now - ts < this.FLOOD_DETECTION_WINDOW_MS
    );
    
    // Alert if we detect a flood pattern (but don't block - existing deduplication handles it)
    if (this.recentConnectionTimestamps.length > this.FLOOD_THRESHOLD) {
      console.error(
        `🚨 [FLOOD-DETECTED] ${this.recentConnectionTimestamps.length} connection attempts in ${this.FLOOD_DETECTION_WINDOW_MS}ms!`,
        'This indicates a bug causing unnecessary WebSocket connections. Check for loops calling getYDocForDoc.'
      );
    }
    
    // Track repeated attempts for same document
    const attemptCount = (this.connectionAttempts.get(docId) || 0) + 1;
    this.connectionAttempts.set(docId, attemptCount);
    
    if (attemptCount > this.CONNECTION_WARNING_THRESHOLD) {
      console.warn(
        `⚠️ [DUPLICATE-CONNECTION] Document ${docId.slice(-8)} connection attempt #${attemptCount}.`,
        'Existing deduplication should handle this, but multiple attempts detected.'
      );
    }
    
    // Clean up old attempt counters (after 30 seconds)
    setTimeout(() => {
      this.connectionAttempts.delete(docId);
    }, 30000);
    
    // Note entities MUST use 'note:' prefix, not 'doc:', for WebSocket persistence.
    // Use caller-supplied type first, then local cache, then fetch as last resort.
    let entityType: string | undefined = knownEntityType || this.entityTypes.get(docId);
    
    if (!entityType) {
      try {
        const entity = await entityService.get(docId);
        entityType = entity?.entityType;
      } catch (err) {
        console.warn(`[ENTITY-TYPE] Failed to fetch entity type for ${docId.slice(-8)}, defaulting to Document`);
        entityType = 'Document';
      }
    }

    if (entityType) {
      this.entityTypes.set(docId, entityType);
    }
    
    // ✅ FIX: Check if document already exists to prevent creating multiple instances
    const docKey = this.getDocKey(docId, entityType);
    const existingDoc = this.crdt.getYDoc(docKey);
    const existingProvider = this.crdt.getProvider(docKey);
    
    log.log(`🔍 [CRDT_TRACE:${crdtTraceId}] Checking for existing Y.Doc:`, {
      docKey,
      hasExistingDoc: !!existingDoc,
      hasExistingProvider: !!existingProvider,
      providerConnected: existingProvider?.wsconnected,
      providerSynced: existingProvider?.synced
    });
    
    if (existingDoc) {
      // Document exists in cache
      log.log(`[DocsCRDT] Found existing Y.Doc for ${docId.slice(-8)}, checking if stale...`);
      
      const yxml = existingDoc.getXmlFragment('prosemirror');
      
      log.log(`📊 [CRDT_TRACE:${crdtTraceId}] Existing Y.Doc state:`, {
        yxmlLength: yxml.length,
        hasInitialBase64: !!initialBase64,
        initialBase64Length: initialBase64?.length || 0
      });
      
      // ✅ STEP 1: Check for complete corruption (empty doc with backend content)
      if (yxml.length === 0 && initialBase64 && initialBase64.length > 100) {
        // If another call is actively initializing this doc, the empty Y.Doc is expected — wait for it
        if (globalInitializationLock.has(docId)) {
          log.log(`[DocsCRDT] ${docId.slice(-8)} Y.Doc is empty but initialization is in progress, waiting for lock`);
          try {
            await this.withTimeout(
              globalInitializationLock.get(docId)!,
              10000,
              `Lock wait (empty-doc) for ${docId.slice(-8)}`
            );
            const initializedDoc = this.crdt.getYDoc(docKey);
            const initializedProvider = this.crdt.getProvider(docKey);
            if (initializedDoc) {
              return { doc: initializedDoc, provider: initializedProvider };
            }
          } catch (lockError) {
            log.log(`[DocsCRDT] Lock wait failed for ${docId.slice(-8)}, proceeding with fresh initialization`);
          }
        }

        console.error(`🚨 [EMPTY-YDOC] ${docId.slice(-8)} - Y.Doc is empty but backend has content!`);
        console.error(`🚨 [CORRUPTION] This indicates Y.Doc corruption - recreating from backend`);
        console.error(`🚨 [CRDT_TRACE:${crdtTraceId}] CORRUPTION DETECTED - will recreate`);
        
        try {
          // Disconnect and destroy old provider
          if (existingProvider) {
            existingProvider.disconnect();
            existingProvider.destroy();
            console.warn(`✅ [RECREATE] Old provider disconnected for ${docId.slice(-8)}`);
          }
          
          // Delete corrupted Y.Doc from cache
          this.crdt.disconnect(docKey);
          console.warn(`✅ [RECREATE] Empty Y.Doc deleted from cache for ${docId.slice(-8)}`);
          console.warn(`🔄 [RECREATE] Will create fresh Y.Doc with backend's correct state...`);
          
          // ✅ CRITICAL: Don't return here - fall through to create NEW Y.Doc below
          
        } catch (cleanupError) {
          console.error(`❌ [RECREATE] Failed to cleanup empty Y.Doc for ${docId.slice(-8)}:`, cleanupError);
          // Continue anyway to try creating fresh doc
        }
      }
      // ✅ STEP 2: SMART CACHE - Version-based stale detection (deterministic)
      // 🚀 PERFORMANCE: Use lightweight version endpoint instead of fetching full document
      // This saves ~500KB-2MB I/O per cache validation check
      else {
        let backendVersion = 0;
        
        try {
          // ⚡ PERFORMANCE: Check cache first (5-second TTL)
          const cached = this.versionCache.get(docId);
          const now = Date.now();
          
          if (cached && now - cached.timestamp < this.VERSION_CACHE_TTL) {
            backendVersion = cached.version;
            log.log(`⚡ [CRDT_TRACE:${crdtTraceId}] Version cache HIT: ${backendVersion} (age: ${now - cached.timestamp}ms)`);
          } else {
            // 🚀 OPTIMIZED: Only fetch version number, not full document with base64
            const versionResponse = await entityService.getVersion(docId);
            backendVersion = versionResponse?.version || 0;
            this.versionCache.set(docId, { version: backendVersion, timestamp: now });
            log.log(`⚡ [CRDT_TRACE:${crdtTraceId}] Version fetched from backend: ${backendVersion}`);
          }
        } catch (fetchError) {
          console.warn(`⚠️ Failed to fetch version for cache check (${docId.slice(-8)}):`, fetchError);
          // Fall back to reusing cached doc if fetch fails
          return { doc: existingDoc, provider: existingProvider };
        }
        
        const cachedVersion = this.docVersions.get(docId) || 0;
        
        log.log(`🔍 [CRDT_TRACE:${crdtTraceId}] Version comparison:`, {
          backendVersion,
          cachedVersion,
          isStale: backendVersion > cachedVersion && backendVersion > 0
        });
        
        // ✅ DETERMINISTIC: Compare versions
        // backendVersion > 0 check prevents false positives for old docs without version field
        if (backendVersion > cachedVersion && backendVersion > 0) {
          console.warn(`🔄 [STALE-VERSION] ${docId.slice(-8)} - Cached v${cachedVersion}, Backend v${backendVersion}`);
          console.warn(`🔄 [STALE-VERSION] Backend has newer version - recreating Y.Doc from backend state`);
          console.warn(`🔄 [CRDT_TRACE:${crdtTraceId}] STALE VERSION - will recreate Y.Doc`);
          
          try {
            // Disconnect and destroy old provider
            if (existingProvider) {
              existingProvider.disconnect();
              existingProvider.destroy();
            }
            
            // Delete stale Y.Doc from cache
            this.crdt.disconnect(docKey);
            
            // Update version tracker with backend version
            this.docVersions.set(docId, backendVersion);
            
            // Now fetch full document with base64 only if we need to recreate
            // This is the only time we actually need the full content
            try {
              const backendEntity = await entityService.get(docId);
              if (backendEntity?.skeleton?.base64) {
                initialBase64 = backendEntity.skeleton.base64;
              }
              
              // ✅ CRITICAL: Mark that we're recreating due to stale version
              this.recreatingFromStaleVersion.set(docId, {
                backendVersion,
                cachedVersion,
                timestamp: Date.now(),
                initialBase64: backendEntity?.skeleton?.base64
              });
            } catch (fetchError) {
              console.warn(`⚠️ Failed to fetch full document for stale recreation:`, fetchError);
              // Continue with whatever initialBase64 was passed in
            }
            
            // ✅ CRITICAL: Fall through to create NEW Y.Doc below with backend state
            
          } catch (cleanupError) {
            console.error(`❌ [STALE-RECREATE] Failed to cleanup stale Y.Doc for ${docId.slice(-8)}:`, cleanupError);
            // Continue anyway to try creating fresh doc
          }
        } else {
          // Version is same or newer in cache - safe to reuse
      return { doc: existingDoc, provider: existingProvider };
        }
      }
    }
    
    // ✅ FIX: Check if initialization is already in progress to prevent race conditions
    if (this.initializationPromises.has(docId)) {
      log.log(`⚡ [CRDT_TRACE:${crdtTraceId}] Reusing in-progress initialization for ${docId.slice(-8)}`);
      debugLog('[getYDocForDoc] ⏳ Initialization already in progress, waiting for completion:', docId);
      // ✅ SMART DEDUPLICATION: Wait for the existing initialization to complete
      // This reuses work instead of creating duplicate Y.Docs
      return await this.initializationPromises.get(docId)!;
    }
    
    // ✅ CORRUPTION PREVENTION: Global lock to prevent simultaneous initialization
    if (globalInitializationLock.has(docId)) {
      log.log(`🔒 [CRDT_TRACE:${crdtTraceId}] === WAITING FOR GLOBAL LOCK ===`);
      log.log('🚨 [CRDT-DEBUG] Document initialization already in progress globally, waiting...', docId.slice(-8));
      const lockStartTime = Date.now();
      
      try {
        // ✅ Wait for lock with 10-second timeout
        await this.withTimeout(
          globalInitializationLock.get(docId)!,
          10000,
          `Lock wait for ${docId.slice(-8)}`
        );
        
        const lockWaitTime = Date.now() - lockStartTime;
        log.log(`🔓 [CRDT_TRACE:${crdtTraceId}] Lock released after ${lockWaitTime}ms`);
        
        // After waiting, check if document now exists
        const nowExistingDoc = this.crdt.getYDoc(docKey);
        const nowExistingProvider = this.crdt.getProvider(docKey);
        if (nowExistingDoc) {
          log.log('🚨 [CRDT-DEBUG] Document now exists after waiting, reusing:', {
            docId: docId.slice(-8),
            yxmlLength: nowExistingDoc.getXmlFragment('prosemirror').length
          });
          return { doc: nowExistingDoc, provider: nowExistingProvider };
        }
      } catch (lockError) {
        console.error(`⚠️ [CRDT] Lock wait failed for ${docId.slice(-8)}:`, lockError);
        // Force-remove stale lock and continue to create fresh doc
        globalInitializationLock.delete(docId);
        log.log(`🔓 [CRDT_TRACE:${crdtTraceId}] Stale lock force-removed, will create fresh doc`);
      }
    }
    
    // Creating NEW Y.js doc and service state (verbose logs removed)
    
    // ✅ CRITICAL FIX: ALWAYS delay WebSocket connection until backend content is applied
    // This ensures backend (source of truth) is loaded first, preventing WebSocket from serving stale data
    const isRecreatingFromStale = this.recreatingFromStaleVersion.has(docId);
    
    log.log(`🔧 [CRDT_TRACE:${crdtTraceId}] Creating NEW Y.Doc and provider...`);
    
    // Set global lock for this document
    const initPromise = (async () => {
      log.log(`🔒 [CRDT_TRACE:${crdtTraceId}] Setting global lock...`);
      
      // ✅ ALWAYS create provider disconnected - connect after backend content is applied
      const doc = this.crdt.ensureInitialized(docKey, {
        connectImmediately: false  // ← Changed: ALWAYS false (was: !isRecreatingFromStale)
      });
      
      log.log(`[CRDT] ${docId.slice(-8)} Y.Doc created, provider disconnected until backend content is applied`);
      if (isRecreatingFromStale) {
        console.warn(`🔄 [STALE-RECONNECT] ${docId.slice(-8)} - Recreating from stale version detected`);
      }
      log.log('[getYDocForDoc] Provider created disconnected', { docId: docId.slice(-8), isRecreatingFromStale });
      
      // Y.js doc created, getting provider (verbose logs removed)
      
      const provider = this.crdt.getProvider(docKey);
      log.log(`📡 [CRDT_TRACE:${crdtTraceId}] Provider retrieved:`, {
        hasProvider: !!provider,
        connected: provider?.wsconnected || false,
        synced: provider?.synced || false
      });
      return { doc, provider };
    })();
    
    globalInitializationLock.set(docId, initPromise);
    
    // 📊 Track lock acquisition time for monitoring
    this.lockMetrics.set(docId, Date.now());
    
    // ✅ CRITICAL: Wrap in try/finally to ensure lock cleanup on error
    let doc: Y.Doc;
    let provider: WebsocketProvider | undefined;
    
    try {
      // ✅ Add timeout protection (30 seconds)
      const result = await this.withTimeout(
        initPromise,
        30000,
        `Y.js initialization for ${docId.slice(-8)}`
      );
      doc = result.doc;
      provider = result.provider;
    
    // 🔧 CRITICAL FIX: Store initial base64 SIZE (decoded bytes) AND element count for size-drop detection
    let initialBase64Size: number | null = null;
    let initialElementCount: number | null = null;
    if (initialBase64) {
      try {
        // Decode to get actual Y.js state size (not base64 string length)
        const bin = atob(initialBase64);
        initialBase64Size = bin.length; // Store actual binary size in bytes
        
        // Also decode to get initial element count (more reliable than byte size)
        const tempDoc = new Y.Doc();
        const update = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) {
          update[i] = bin.charCodeAt(i);
        }
        Y.applyUpdateV2(tempDoc, update);
        const tempYxml = tempDoc.getXmlFragment('prosemirror');
        initialElementCount = tempYxml.length;
        console.warn(`📊 [INITIAL-STATE] ${docId.slice(-8)} - Initial content: ${initialElementCount} elements, ${bin.length} bytes`);
      } catch (e) {
        console.warn(`⚠️ [SIZE-TRACKING] ${docId.slice(-8)} - Could not decode initialBase64 for size tracking`);
      }
    }
    
    // Provider result (verbose logs removed)
    
    // 🔍 DEBUG: Check for existing provider listeners before adding (CONFLICT DETECTION)
    if (provider) {
      // ✅ Type-safe: Create const reference for use in callbacks
      const providerRef = provider;
      
      const statusListeners = (provider as any)._callbacks?.status?.length || 0;
      const syncListeners = (provider as any)._callbacks?.sync?.length || 0;
      // Adding docsCRDTService provider listeners (verbose logs removed)
      
      // Single unified status handler: reset failure count, throttled reconnect save
      let isInitialConnect = true;
      providerRef.on('status', ({ status }: { status: string }) => {
        if (status === 'connected') {
          this.wsFailureCounts.set(docId, 0);
          if (isInitialConnect) {
            isInitialConnect = false;
            log.log(`[CRDT] ${docId.slice(-8)} initial WS connection established`);
            return;
          }
          // REconnection: throttled save to push latest state to server
          const now = Date.now();
          const lastSave = this.lastReconnectSaveTime.get(docId) || 0;
          if (now - lastSave > 30000) {
            const yxml = doc.getXmlFragment('prosemirror');
            if (yxml.length > 0) {
              this.lastReconnectSaveTime.set(docId, now);
              this.triggerDebouncedSave(docId, yxml);
              log.log(`[CRDT] ${docId.slice(-8)} reconnected, triggering save`);
            }
          }
        } else if (status === 'disconnected') {
          log.log(`[CRDT] ${docId.slice(-8)} WS disconnected, will auto-reconnect`);
        }
      });
      
      // 🚨 LISTEN FOR CORRUPTION NOTIFICATIONS from WebSocket server
      // @ts-ignore - WebSocket provider doesn't have types for custom messages
      // ✅ CORRUPTION DETECTION: Removed - Smart cache with version tracking handles staleness
      // Old size-drop detection was disabled in WebSocket server (too aggressive, false positives)
      // Version-based stale detection is more reliable and doesn't require page reloads
      
      // Keep listener for potential future server messages, but don't auto-reload
      providerRef.ws?.addEventListener('message', (event: MessageEvent) => {
        try {
          // Try to parse as JSON (server notifications are JSON)
          if (typeof event.data === 'string') {
            const message = JSON.parse(event.data);
            
            // Log any server messages for debugging, but don't auto-reload
            if (message.type) {
              log.log(`[WebSocket Message] ${docId.slice(-8)} received:`, message.type);
            }
          }
        } catch (e) {
          // Not JSON or not our message - ignore (Y.js uses binary messages)
        }
      });
      
      providerRef.on('sync', (isSynced: boolean) => {
        if (isSynced) {
          const yxml = doc.getXmlFragment('prosemirror');
          const currentSize = yxml.length;
          const expectedSize = this.expectedDocumentSizes.get(docId);
          
          log.log(`[CRDT] ${docId.slice(-8)} WS synced, elements=${currentSize} expected=${expectedSize ?? 'none'}`);
          
          // Detect significant content loss or empty-after-sync and recover from backend
          const wasExpectingContent = expectedSize && expectedSize > 0;
          const hasContentLoss = wasExpectingContent && currentSize < expectedSize * 0.5;
          const isEmptyAfterSync = wasExpectingContent && currentSize === 0;

          if (hasContentLoss || isEmptyAfterSync) {
            const lastEditTime = this.lastLocalEditTime.get(docId) || 0;
            const userHasRecentEdits = Date.now() - lastEditTime < 10000;

            if (userHasRecentEdits) {
              console.warn(`[SYNC] ${docId.slice(-8)} content loss detected but user has recent edits — skipping recovery`);
              return;
            }

            console.warn(`[SYNC] ${docId.slice(-8)} content loss: expected=${expectedSize} got=${currentSize}, recovering from backend`);
            (async () => {
              try {
                const recovered = await this.hydrateFromBackend(docId, doc, yxml, 'sync-recovery');
                if (recovered) await this.performValidatedSave(docId, yxml);
                else console.warn(`[SYNC] ${docId.slice(-8)} backend also empty — recovery skipped`);
              } catch (err) {
                console.error(`[SYNC] ${docId.slice(-8)} recovery failed:`, err);
              }
            })();
          }
        }
      });
      
      // Handle connection errors: attempt REST recovery when WS is persistently down
      provider.on('connection-error', () => {
        const failCount = (this.wsFailureCounts.get(docId) || 0) + 1;
        this.wsFailureCounts.set(docId, failCount);

        const yxml = doc.getXmlFragment('prosemirror');
        if (failCount <= 3 || failCount % 10 === 0) {
          console.warn(`[CRDT] ${docId.slice(-8)} WS connection error #${failCount} (elements=${yxml.length})`);
        }

        if (
          yxml.length === 0 &&
          failCount >= this.WS_FAILURE_RECOVERY_THRESHOLD &&
          !this.wsRecoveryInFlight.has(docId)
        ) {
          this.wsRecoveryInFlight.add(docId);
          console.warn(`[CRDT] ${docId.slice(-8)} WS failed ${failCount}x with empty doc, recovering from REST...`);
          (async () => {
            try {
              const recovered = await this.hydrateFromBackend(docId, doc, yxml, 'ws-down-rest-recovery');
              if (recovered) this.wsFailureCounts.set(docId, 0);
              else console.warn(`[CRDT] ${docId.slice(-8)} REST also empty, will retry`);
            } catch (err) {
              console.error(`[CRDT] ${docId.slice(-8)} REST recovery failed:`, err);
            } finally {
              this.wsRecoveryInFlight.delete(docId);
            }
          })();
        }
      });
    }
    
    // 🛡️ CRITICAL: Add Y.Doc error handler to gracefully handle internal Y.js errors
    // This catches errors that occur during WebSocket message decoding
    // @ts-ignore - Y.Doc does support 'error' event but TypeScript types are incomplete
    doc.on('error', (error: Error) => {
      console.error(`🚨 [Y.DOC-ERROR] ${docId.slice(-8)} - Y.js internal error:`, error.message);
      console.error(`🚨 [Y.DOC-ERROR] Stack:`, error.stack);
      
      // Check if this is a decoding/corruption error
      const isDecodingError = error.message?.includes('Unexpected end of array') ||
                              error.message?.includes('decoding') ||
                              error.message?.includes('decoder') ||
                              error.message?.includes('corrupt') ||
                              error.message?.includes('Invalid');
      
      if (isDecodingError) {
        console.error(`🚨 [CORRUPTION] ${docId.slice(-8)} - Corrupted binary data detected from WebSocket`);
        console.error(`🚨 [CORRUPTION] This means the server sent invalid Y.js binary data`);
        
        // Mark document as corrupted to prevent save loops
        this.corruptedDocuments.add(docId);
        console.warn(`⚠️ [CORRUPTION] ${docId.slice(-8)} - Marked as corrupted, saves will be blocked`);
        
        // Disconnect provider to stop receiving more corrupted data
        if (provider) {
          try {
            console.warn(`🔌 [CORRUPTION] Disconnecting WebSocket provider for ${docId.slice(-8)}`);
            provider.disconnect();
            log.log(`✅ [CORRUPTION] Provider disconnected for ${docId.slice(-8)}`);
          } catch (disconnectError) {
            console.error(`❌ [CORRUPTION] Failed to disconnect provider:`, disconnectError);
          }
        }
        
        // Log recovery instructions
        console.warn(`📋 [RECOVERY-NEEDED] ${docId.slice(-8)} - Automatic recovery options:`);
        console.warn(`   1. Refresh the page (will attempt backend recovery)`);
        console.warn(`   2. Check browser console for document ID`);
        console.warn(`   3. Verify database integrity in both 'entities' and 'yjs_documents' collections`);
        console.warn(`   4. Contact support if issue persists`);
        
        // Clear corrupted content and recover from backend
        const yxml = doc.getXmlFragment('prosemirror');
        doc.transact(() => { yxml.delete(0, yxml.length); }, 'corruption-clear');
        setTimeout(async () => {
          try {
            const recovered = await this.hydrateFromBackend(docId, doc, yxml, 'corruption-recovery');
            if (recovered) {
              this.corruptedDocuments.delete(docId);
              console.warn(`[CRDT] ${docId.slice(-8)} corruption recovery restored ${yxml.length} elements`);
            } else {
              console.error(`[CRDT] ${docId.slice(-8)} corruption recovery: backend also empty`);
            }
          } catch (err) {
            console.error(`[CRDT] ${docId.slice(-8)} corruption recovery failed:`, err);
          }
        }, 1000);
      }
    });
    
    // CRITICAL: Set up awareness IMMEDIATELY after provider creation
    if (provider) {
      debugLog('[DocsApp - CRDT] 🔍 MULTIPLAYER DEBUG: Provider created for doc:', {
        docId: docId.slice(-8),
        docKey,
        roomName: provider.roomname,
        websocketUrl: provider.url,
        connected: provider.wsconnected,
        synced: provider.synced,
        awarenessStates: provider.awareness?.getStates?.()?.size || 0
      });
      
      // REMOVED: WebSocket message validation (was causing performance issues)
      
      await this.setupUserAwareness(provider, docId, doc);
      // ✅ CRITICAL FIX: Reduced suppression from 1500ms to 300ms
      // 1500ms was too long - users could paste and refresh before save completes
      // 300ms is enough to prevent save during initial load, but allows fast saves after
      this.saveSuppressionUntil.set(docId, Date.now() + 300);
      log.log(`🔕 [SAVE_SUPPRESSION] Saves suppressed for ${docId.slice(-8)} until ${new Date(Date.now() + 300).toISOString()}`);
      log.log(`⏰ [SAVE_SUPPRESSION] Suppression will expire in 300ms, then edits will auto-save`);
    } else {
      log.warn('[DocsApp - CRDT] No provider available for doc:', docId);
      // Try again after a short delay
      setTimeout(async () => {
        const delayedProvider = this.crdt.getProvider(docKey);
        if (delayedProvider) {
          debugLog('[DocsApp - CRDT] 🔍 MULTIPLAYER DEBUG: Delayed provider found:', {
            docId: docId.slice(-8),
            docKey,
            roomName: delayedProvider.roomname,
            websocketUrl: delayedProvider.url,
            connected: delayedProvider.wsconnected,
            synced: delayedProvider.synced,
            awarenessStates: delayedProvider.awareness?.getStates?.()?.size || 0
          });
          // REMOVED: WebSocket message validation (was causing performance issues)
          await this.setupUserAwareness(delayedProvider, docId, doc);
          // ✅ CRITICAL FIX: Reduced suppression from 1500ms to 300ms
          this.saveSuppressionUntil.set(docId, Date.now() + 300);
          log.log(`🔕 [SAVE_SUPPRESSION] Saves suppressed for ${docId.slice(-8)} (delayed provider) until ${new Date(Date.now() + 300).toISOString()}`);
        }
      }, 100);
    }
    
    // Get or create Y.XmlFragment
    const yxml = doc.getXmlFragment('prosemirror');
    const wasAlreadyCached = this.yContentCache.has(docId);
    this.yContentCache.set(docId, yxml);
    
    debugLog('[DocsApp - CRDT] Y.XmlFragment access:', {
      docId: docId.slice(-8),
      wasAlreadyCached,
      yxmlLength: yxml.length,
      hasInitialContent: !!(initialBase64 || initialContent)
    });
    
    // ✅ FIX: Initialize based on document emptiness, not cache status
    const isDocumentEmpty = yxml.length === 0;
    let hasContentToLoad = !!(initialBase64 || initialContent);
    
    console.warn(`[DocsCRDT] 📊 Y.Doc state before initialization:`, {
      docId: docId.slice(-8),
      isDocumentEmpty,
      hasContentToLoad,
      wasAlreadyCached,
      yxmlLength: yxml.length
    });
    
    // If document is empty and no initial content provided, attempt recovery from backend
    if (isDocumentEmpty && !hasContentToLoad && docId && !this.corruptedDocuments.has(docId)) {
      console.warn(`[CRDT] ${docId.slice(-8)} is empty with no content to load, attempting backend recovery`);
      try {
        const freshEntity = await entityService.get(docId);
        if (freshEntity?.skeleton?.base64) {
          initialBase64 = freshEntity.skeleton.base64;
          hasContentToLoad = true;
          if (freshEntity.skeleton.version) {
            this.docVersions.set(docId, freshEntity.skeleton.version);
          }
        } else if (freshEntity?.skeleton?.content) {
          initialContent = freshEntity.skeleton.content;
          hasContentToLoad = true;
        }
      } catch (error) {
        console.error(`[CRDT] ${docId.slice(-8)} init recovery failed:`, error);
      }
    }
    
    if (isDocumentEmpty && hasContentToLoad) {
      log.log(`🔧 [CRDT_TRACE:${crdtTraceId}] === INITIALIZING DOCUMENT CONTENT ===`);
      console.warn(`[DocsCRDT] 🔧 INITIALIZING DOCUMENT CONTENT for ${docId.slice(-8)}`);
      console.warn(`[DocsCRDT] 📊 Content sources available:`, {
        hasBase64: !!initialBase64,
        base64Length: (initialBase64 || '').length,
        hasHtmlContent: !!initialContent,
        htmlLength: (typeof initialContent === 'string' ? initialContent.length : 0),
        preferredSource: initialBase64 ? '🎯 Y.js binary (base64)' : '⚠️ HTML fallback',
        willLoadWelcomeImage: includeWelcomeImage,
        hasPageMargins: !!pageMargins
      });
      
      const initContentStartTime = Date.now();
      const initPromise = this.initializeDocumentContent(doc, yxml, docId, initialContent, initialBase64, includeWelcomeImage, pageMargins);
      this.initializationPromises.set(docId, initPromise.then(() => ({ doc, provider })));
      
      log.log(`⏳ [CRDT_TRACE:${crdtTraceId}] Awaiting content initialization...`);
      
      // ✅ CRITICAL FIX: MUST await initialization before returning doc
      // Without this await, doc_write gets a Y.Doc with uninitialized Y.XML structure
      // causing "Invalid access: Add Yjs type to a document before reading data" errors
      await initPromise;
      
      const initContentDuration = Date.now() - initContentStartTime;
      log.log(`✅ [CRDT_TRACE:${crdtTraceId}] Content initialization complete (${initContentDuration}ms)`);
      
      // Clean up promise after completion
      const finalSize = yxml.length;
      console.warn(`[DocsCRDT] ✅ Initialization completed for ${docId.slice(-8)}, final size: ${finalSize} elements`);
      this.initializationPromises.delete(docId);
    } else if (!isDocumentEmpty) {
      console.warn(`[DocsCRDT] ℹ️ Document ${docId.slice(-8)} already has content (${yxml.length} elements), skipping initialization`);
    } else {
      console.warn(`[DocsCRDT] ℹ️ Document ${docId.slice(-8)} is empty and no content to load - new document`);
    }

    // ✅ CRITICAL FIX: ALWAYS connect WebSocket provider AFTER backend content is loaded
    // This prevents stale server state from overwriting freshly loaded backend content
    // Backend is the source of truth - WebSocket is only for real-time sync
    log.log(`[CRDT] ${docId.slice(-8)} connecting provider (elements=${yxml.length})`);

    // Connect WebSocket AFTER backend content is applied (backend is source of truth)
    this.crdt.connectProvider(docKey);
    if (isRecreatingFromStale) {
      this.recreatingFromStaleVersion.delete(docId);
    }
    log.log(`[CRDT] ${docId.slice(-8)} WS provider connected`);


    // Set document name if provided (only thing we keep from legacy system)
    if (initialName) {
      const nameText = doc.getText('name');
      if (nameText.length === 0) {
        nameText.insert(0, initialName);
        debugLog('[DocsApp - CRDT] Set document name:', initialName);
      }
    }

    debugLog('[DocsApp - CRDT] Document ready:', { 
      wsconnected: provider?.wsconnected, 
      synced: provider?.synced,
      hasContent: yxml.length > 0,
      hasInitialContent: !!(initialBase64 || initialContent),
      wasAlreadyCached,
      docId: docId.slice(-8)
    });

    // 🚨 MULTI-TAB PROTECTION: Mark that this tab has authoritative content from backend
    // This prevents other tabs from overwriting our freshly loaded content during initialization
    if (yxml.length > 0 || initialBase64) {
      (doc as any).__hasAuthoritativeContent = true;
      (doc as any).__contentLoadedAt = Date.now();
      log.log(`🛡️ [MULTI-TAB-PROTECTION] ${docId.slice(-8)} - Tab marked as having authoritative content:`, {
        elements: yxml.length,
        hasBase64: !!initialBase64,
        timestamp: new Date().toISOString()
      });
    }
    
    // Set up an observer for Y.js document changes to auto-persist (if not already set up)
    if (!this.activeDocObservers.has(docId)) {
      log.log(`🔔 [SAVE_OBSERVER] Setting up document observer for ${docId.slice(-8)}`);
      this.setupDocObserver(docId, doc);
      log.log(`✅ [SAVE_OBSERVER] Document observer active for ${docId.slice(-8)} - will trigger saves on edits`);
      debugLog('[DocsApp - CRDT] Set up document observer for auto-persistence:', docId.slice(-8));
    } else {
      log.log(`✅ [SAVE_OBSERVER] Document observer already exists for ${docId.slice(-8)}`);
    }
    

      const crdtDuration = Date.now() - crdtStartTime;
      
      log.log(`🟢 [CRDT_TRACE:${crdtTraceId}] === CRDT.GETYDOCFORDOC COMPLETE (${crdtDuration}ms) ===`, {
        docId: docId.slice(-8),
        contentElements: yxml.length,
        expectedSize: this.expectedDocumentSizes.get(docId),
        wsConnected: provider?.wsconnected || false,
        wsSynced: provider?.synced || false,
        hasInitialContent: !!(initialBase64 || initialContent),
        wasAlreadyCached,
        isNewDocument: yxml.length === 0 && !initialBase64 && !initialContent,
        duration: crdtDuration
      });
      
      // ✅ CRITICAL FIX: Initialize docVersions to prevent stale detection on immediate re-access
      // For new documents created via docs_create with initialContent, backend already has version 1
      // Set docVersions optimistically to avoid unnecessary recreation when editor loads
      if (!wasAlreadyCached && !this.docVersions.has(docId)) {
        this.docVersions.set(docId, 1);
        log.log(`🔧 [CRDT_TRACE:${crdtTraceId}] Set initial version tracker to 1 (prevents stale detection)`);
      }

      return {doc, provider};
      
    } catch (error) {
      console.error(`❌ [CRDT] Initialization failed for ${docId.slice(-8)}:`, error);
      
      // Clean up partial state
      const docKey = this.getDocKey(docId, entityType);
      try {
        this.crdt.disconnect(docKey);
        this.initializationPromises.delete(docId);
        this.connectionAttempts.delete(docId);
      } catch (cleanupError) {
        console.error(`[CRDT] Cleanup error for ${docId.slice(-8)}:`, cleanupError);
      }
      
      throw error; // Re-throw to caller
      
    } finally {
      // ✅ CRITICAL: Always clean up lock, success or failure
      log.log(`🔓 [CRDT_TRACE:${crdtTraceId}] Releasing global lock`);
      globalInitializationLock.delete(docId);
      
      // 📊 Track lock duration for performance monitoring
      const lockStartTime = this.lockMetrics.get(docId);
      this.lockMetrics.delete(docId);

      if (lockStartTime) {
        const lockDuration = Date.now() - lockStartTime;
        if (lockDuration > 5000) {
          console.warn(`⚠️ [CRDT-PERF] Slow initialization: ${docId.slice(-8)} took ${lockDuration}ms`);
        }
      }
    }
  }

  // Setup an observer for document changes to automatically persist to DB
  private setupDocObserver(docId: string, doc: Y.Doc) {
    const existingCleanup = this.activeDocObservers.get(docId);
    if (existingCleanup) {
      existingCleanup();
      this.activeDocObservers.delete(docId);
    }

    const yxml = doc.getXmlFragment('prosemirror');

    const handler = (update: Uint8Array, origin: any) => {
      if (!update || update.length === 0) return;

      // Skip remote updates from the WebSocket provider
      const isRemoteUpdate = origin && (
        origin instanceof WebsocketProvider ||
        origin.constructor?.name === 'WebsocketProvider' ||
        (origin.ws && origin.awareness)
      );
      if (isRemoteUpdate) return;

      // Respect save suppression window set after initial load
      if (Date.now() < (this.saveSuppressionUntil.get(docId) || 0)) return;

      this.lastLocalEditTime.set(docId, Date.now());

      // Warn if content count drops significantly (deletion detection)
      const currentLength = yxml.length;
      const prevLength = this.previousLengths.get(docId) || 0;
      this.previousLengths.set(docId, currentLength);
      if (prevLength > 0 && currentLength < prevLength - 1) {
        console.warn(`[CRDT] ${docId.slice(-8)} element count dropped: ${prevLength} → ${currentLength}`);
      }

      this.triggerDebouncedSave(docId, yxml);
    };

    doc.on('update', handler);
    this.activeDocObservers.set(docId, () => { doc.off('update', handler); });
  }
  

  getContent(docId: string): Y.XmlFragment | null {
    return this.yContentCache.get(docId) || null;
  }

  getProvider(docId: string, entityType?: string): WebsocketProvider | undefined {
    // Use cached entity type if not provided
    const type = entityType || this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, type);
    return this.crdt.getProvider(docKey);
  }

  async observeDoc(docId: string, callback: (update: DocUpdate) => void) {
    const { doc } = await this.getYDocForDoc(docId);
    
    // Wait for Prosemirror to create the Y.Text type
    const waitForContent = () => {
      return new Promise<Y.Text>((resolve) => {
        if (doc.share.has('content')) {
          resolve(doc.getText('content'));
        } else {
          const observer = () => {
            if (doc.share.has('content')) {
              doc.off('afterTransaction', observer);
              resolve(doc.getText('content'));
            }
          };
          doc.on('afterTransaction', observer);
        }
      });
    };

    const text = await waitForContent();
    const observer = () => {
      this.yContentCache.set(docId, doc.getXmlFragment('prosemirror'));
      // 🚨 CRITICAL FIX: DO NOT use text.toString() - Y.Text could contain formatting contamination!
      let safeContent = '';
      try {
        const anyText = text as any;
        if (anyText._content && typeof anyText._content === 'string') {
          safeContent = anyText._content;
        } else {
          console.warn('[docsCRDTService] 🚨 Using dangerous toString() in observer callback!');
          const contaminated = text.toString();
          safeContent = contaminated
            .replace(/<fontFamily[^>]*>.*?<\/fontFamily>/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/fontFamily/g, '');
        }
      } catch (error) {
        console.error('[docsCRDTService] CRITICAL: Cannot extract text safely in observer:', error);
        safeContent = '';
      }
      callback({ content: safeContent });
    };

    doc.on('update', observer);
    // await this.crdt.connect(docId, doc);

    return () => {
      doc.off('update', observer);
      const entityType = this.entityTypes.get(docId);
      this.crdt.disconnect(this.getDocKey(docId, entityType));
    };
  }

  cleanup(docId: string) {
    // Remove document observer if exists
    const cleanup = this.activeDocObservers.get(docId);
    if (cleanup) {
      cleanup();
      this.activeDocObservers.delete(docId);
    }
    
    // Flush and cleanup per-document debounce to ensure pending saves complete
    const debouncedFn = this.perDocumentDebouncedSaves.get(docId);
    if (debouncedFn) {
      try {
        debouncedFn.flush();
        debouncedFn.cancel();
        this.perDocumentDebouncedSaves.delete(docId);
      } catch (e) {
        // Ignore cleanup errors
      }
    }
    
    // Clean up awareness event listeners if they exist
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const provider = this.crdt.getProvider(docKey);
    if (provider && (provider as any).__awarenessCleanup) {
      try {
        (provider as any).__awarenessCleanup();
        delete (provider as any).__awarenessCleanup;
        debugLog(`[docsCRDT] Cleaned up awareness listeners for doc ${docId}`);
      } catch (error) {
        log.warn('[docsCRDT] Error cleaning up awareness listeners:', error);
      }
    }
    
    // Clear local cache but DON'T disconnect the provider
    // This allows multiplayer to continue working across component remounts
    this.yContentCache.delete(docId);
    
    // 🔥 NEW: Clean up reconnect throttle tracking
    this.lastReconnectSaveTime.delete(docId);
    
    debugLog(`[docsCRDT] Cleaned up local state for doc ${docId} (keeping provider alive for multiplayer)`);
    
    // REMOVED: this.crdt.disconnect(key) - this was breaking multiplayer
    // The provider should stay connected to maintain multiplayer functionality
  }

  // New method for complete cleanup (only use when document is permanently closed)
  completeCleanup(docId: string) {
    // First do regular cleanup
    this.cleanup(docId);
    
    // Then disconnect the provider completely
    try {
      const entityType = this.entityTypes.get(docId);
      const key = this.getDocKey(docId, entityType);
      this.crdt.disconnect(key);
      
      // Clean up cached entity type
      this.entityTypes.delete(docId);
      debugLog(`[docsCRDT] Completely disconnected CRDT provider for ${key}`);
    } catch (error) {
      log.error('[docsCRDT] Failed to disconnect CRDT provider:', error);
    }
  }

  /**
   * Mark that a component (typically an editor) now depends on this doc's
   * Y.Doc + WebsocketProvider. Increments the reference count and cancels any
   * pending disconnect scheduled by a prior release.
   */
  acquireDoc(docId: string): void {
    if (!docId) return;
    const pending = this.pendingReleases.get(docId);
    if (pending) {
      clearTimeout(pending);
      this.pendingReleases.delete(docId);
    }
    this.refCounts.set(docId, (this.refCounts.get(docId) ?? 0) + 1);
  }

  /**
   * Release a reference acquired via `acquireDoc`. Always runs the lightweight
   * `cleanup` path immediately (detaches the editor's observers). When the
   * count hits zero, schedules a full `completeCleanup` after a grace period
   * so StrictMode and fast remount patterns don't cause WS churn.
   */
  releaseDoc(docId: string): void {
    if (!docId) return;
    const current = this.refCounts.get(docId) ?? 0;
    const next = Math.max(0, current - 1);
    if (next > 0) {
      this.refCounts.set(docId, next);
      return;
    }
    this.refCounts.delete(docId);

    try { this.cleanup(docId); } catch (error) {
      log.error('[docsCRDT] releaseDoc: lightweight cleanup failed:', error);
    }

    const existing = this.pendingReleases.get(docId);
    if (existing) clearTimeout(existing);

    const timer = setTimeout(() => {
      this.pendingReleases.delete(docId);
      if (this.refCounts.has(docId)) return;
      try { this.completeCleanup(docId); } catch (error) {
        log.error('[docsCRDT] releaseDoc: completeCleanup failed:', error);
      }
    }, this.RELEASE_GRACE_MS);
    this.pendingReleases.set(docId, timer);
  }

  // Force an immediate save - use this when you need to ensure content is saved right away
  async forceSave(docId: string, content: string, cleanAttributes = true): Promise<void> {
    try {
      // Clean the content before saving to remove any pending change attributes
      const cleanedContent = this.cleanChangeAttributes(content, !cleanAttributes);
      debugLog(`[docsCRDT] Forcing immediate save to DB for doc ${docId}`);
      await entityService.mergeUpdate(docId, {
        skeleton: {
          '@type': 'Doc',
          content: cleanedContent
        }
      });
      debugLog(`[docsCRDT] Successfully saved content to DB for doc ${docId}`);
    } catch (error) {
      log.error(`[docsCRDT] Failed to save content to DB for doc ${docId}:`, error);
      throw error;
    }
  }

  async updateDocContent(docId: string, content: string) {
    const docResult = await this.getYDocForDoc(docId);
    if (!docResult.doc) {
      log.error('[DocsCRDTService] No doc found for:', docId);
      return;
    }

    // Let Prosemirror handle the Y.Text updates
    // Only save to backend
    this.triggerDebouncedSave(docId, docResult.doc.getXmlFragment('prosemirror'));
  }

  saveToDB(docId: string, content: XmlFragment) {
    log.log(`[docsCRDT] saveToDB called for doc ${docId} (${content.length} chars)`);
    this.triggerDebouncedSave(docId, content);
  }

  // Explicit method to clean and save when accepting/rejecting changes
  async cleanAndSave(docId: string, yxml: Y.XmlFragment): Promise<void> {
    try {
      log.log(`[docsCRDT] cleanAndSave - explicitly cleaning change attributes for doc ${docId}`);
      
      // Convert Y.js to ProseMirror doc with error handling
      let doc: any;
      let meta: any;
      try {
        const result = initProseMirrorDoc(yxml, schema);
        doc = result.doc;
        meta = result.meta;
      } catch (syncError) {
        log.error(`[docsCRDT] Failed to convert Y.js to ProseMirror for doc ${docId}:`, syncError);
        
        // 🚨 CRITICAL: DO NOT USE FALLBACK DOCUMENT CREATION
        // Creating fallback documents can mask underlying YJS corruption issues
        // and lead to data loss or inconsistent document states
        
        throw new Error(`Critical YJS to ProseMirror conversion failure for document ${docId}: ${syncError instanceof Error ? syncError.message : 'Unknown error'}. This indicates document corruption and must be investigated.`);
        
        // ❌ REMOVED: Fallback document creation
        // Creating empty fallback documents masks real issues and can cause data loss
      }
      
      const serializer = DOMSerializer.fromSchema(schema);
      const fragment = serializer.serializeFragment(doc.content);
      
      // 🎯 UNICODE NATIVE: Use XMLSerializer instead of innerHTML to preserve Unicode characters
      // innerHTML automatically converts Unicode (—, ", ') to HTML entities (&mdash;, &quot;, &#39;)
      // XMLSerializer preserves the original Unicode characters
      const xmlSerializer = new XMLSerializer();
      const html = Array.from(fragment.childNodes)
        .map(node => xmlSerializer.serializeToString(node))
        .join('');
      
      // Force clean change attributes  
      const cleanedContent = this.cleanChangeAttributes(html, false);
      
      let base64 = '';
      if (yxml.doc) {
        const update = Y.encodeStateAsUpdateV2(yxml.doc); // 🔧 V2 API consistency
        // ✅ OPTIMIZED: Use array.join() instead of string concatenation for better performance
        const uint8Array = new Uint8Array(update);
        const binaryChunks: string[] = [];
        const chunkSize = 8192; // Process in 8KB chunks to avoid stack overflow
        
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.slice(i, i + chunkSize);
          binaryChunks.push(String.fromCharCode(...chunk));
        }
        const binaryString = binaryChunks.join('');
        
        base64 = btoa(binaryString);
      }
      
      const skeletonPayload: Record<string, unknown> = {
        '@type': 'Doc',
        content: cleanedContent
      };
      if (!SERVER_IS_DURABILITY_BOUNDARY) {
        skeletonPayload.base64 = base64;
      }
      await entityService.mergeUpdate(docId, {
        skeleton: skeletonPayload
      });
      
      log.log(`[docsCRDT] Successfully cleaned and saved doc ${docId}`);
    } catch (error) {
      log.error(`[docsCRDT] Failed to clean and save doc ${docId}:`, error);
      throw error;
    }
  }

  async disconnect() {
    // Flush all per-document debounces to ensure pending saves complete
    for (const [, debouncedFn] of this.perDocumentDebouncedSaves.entries()) {
      try {
        debouncedFn.flush();
        debouncedFn.cancel();
      } catch (e) {
        // Ignore flush errors during disconnect
      }
    }
    this.perDocumentDebouncedSaves.clear();
    
    // Clear all document observers
    for (const [docId, cleanup] of this.activeDocObservers.entries()) {
      cleanup();
    }
    this.activeDocObservers.clear();
    
    this.crdt.cleanup();
  }

  // Clean change attributes from content before persisting to database
  // Only clean attributes when explicitly requested (e.g., when accepting/rejecting changes)
  private cleanChangeAttributes(content: string, preserveChangeAttributes = true): string {
    if (!content) return content;
    
    // Add detailed logging to debug attribute preservation
    log.log('[cleanChangeAttributes] called:', {
      contentLength: content.length,
      preserveChangeAttributes,
      contentSample: content.substring(0, 200)
    });
    
    // If preserveChangeAttributes is true, don't clean them - this allows pending changes to persist
    if (preserveChangeAttributes) {
      // Check if there are any active change attributes in the content
      const hasChangeAttributes = /(?:data-revision-id|revision_id|data-change-status|change_status)="[^"]*"/.test(content);
      
      log.log('[cleanChangeAttributes] Checking for change attributes:', {
        hasChangeAttributes,
        regex: '/(?:data-revision-id|revision_id|data-change-status|change_status)="[^"]*"/',
        foundMatches: content.match(/(?:data-revision-id|revision_id|data-change-status|change_status)="[^"]*"/g) || []
      });
      
      if (hasChangeAttributes) {
        log.log('[cleanChangeAttributes] PRESERVING change attributes in content for pending changes');
        return content; // Return content unchanged to preserve change tracking
      } else {
        log.log('[cleanChangeAttributes] No change attributes found to preserve');
      }
    }
    
    try {
      // Remove revision_id and change_status attributes from all elements
      let cleaned = content;
      
      // Remove data-revision-id attributes
      cleaned = cleaned.replace(/\s*data-revision-id="[^"]*"/g, '');
      
      // Remove data-change-status attributes  
      cleaned = cleaned.replace(/\s*data-change-status="[^"]*"/g, '');
      
      // Remove revision_id attributes (direct XML attributes)
      cleaned = cleaned.replace(/\s*revision_id="[^"]*"/g, '');
      
      // Remove change_status attributes (direct XML attributes)
      cleaned = cleaned.replace(/\s*change_status="[^"]*"/g, '');
      
      // Remove batch-related attributes
      cleaned = cleaned.replace(/\s*batch_description="[^"]*"/g, '');
      cleaned = cleaned.replace(/\s*batch_index="[^"]*"/g, '');
      cleaned = cleaned.replace(/\s*batch_total="[^"]*"/g, '');
      
      // Clean up any double spaces that might result from attribute removal
      cleaned = cleaned.replace(/\s+/g, ' ');
      
      // Reduced logging frequency to prevent spam
      if (Math.random() < 0.1) {
        log.log('[cleanChangeAttributes] Cleaned change attributes from content:', {
          originalLength: content.length,
          cleanedLength: cleaned.length,
          attributesRemoved: content.length - cleaned.length
        });
      }
      
      return cleaned;
    } catch (error) {
      log.error('[docsCRDT] Error cleaning change attributes:', error);
      return content; // Return original if cleaning fails
    }
  }

  /**
   * Get the CRDT room key for a document.
   * Uses 'note:' prefix for Note entities and 'doc:' prefix for all others.
   * Without the correct prefix, the WebSocket creates an empty room and wipes client content.
   */
  private getDocKey(docId: string, entityType?: string): string {
    if (entityType === 'Note' || entityType === 'Content.Note') {
      return `note:${docId}`;
    }
    return `doc:${docId}`;
  }

  /**
   * Fetch the latest Y.js state from the backend REST API and apply it to the given doc.
   * Returns true if recovery succeeded with meaningful content.
   */
  private async hydrateFromBackend(
    docId: string, doc: Y.Doc, yxml: Y.XmlFragment, reason: string
  ): Promise<boolean> {
    const freshEntity = await entityService.get(docId);
    const freshBase64 = freshEntity?.skeleton?.base64;
    if (!freshBase64 || freshBase64.length < 50) return false;
    const bin = atob(freshBase64);
    const update = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) update[i] = bin.charCodeAt(i);
    doc.transact(() => {
      Y.applyUpdateV2(doc, update);
    }, reason);
    this.expectedDocumentSizes.set(docId, yxml.length);
    return yxml.length > 0;
  }

  // Public method for testing - exposes provider access
  getProviderForTesting(docKey: string) {
    return this.crdt.getProvider(docKey);
  }

  // Public method to reset a corrupted document
  resetDocument(docId: string) {
    log.log('🔧 Resetting corrupted Y.js document:', docId);
    
    // Clear from cache
    this.yContentCache.delete(docId);
    
    // Clear any active observers
    const existingCleanup = this.activeDocObservers.get(docId);
    if (existingCleanup) {
      existingCleanup();
      this.activeDocObservers.delete(docId);
    }
    
    // Get the Y.js document and reset it
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const doc = this.crdt.getYDoc(docKey);
    
    // Clean up cached entity type
    this.entityTypes.delete(docId);
    if (doc) {
      const yxml = doc.getXmlFragment('prosemirror');
      yxml.delete(0, yxml.length); // Clear all content
      log.log('✅ Cleared Y.js document content');
      return true;
    } else {
      log.log('❌ No Y.js document found');
      return false;
    }
  }


  /**
   * Initialize document content in a race-condition-safe way
   */
  private async initializeDocumentContent(
    doc: Y.Doc, 
    yxml: Y.XmlFragment, 
    docId: string, 
    initialContent?: string | any, // Support both string (HTML/Markdown) and ProseMirror JSON
    initialBase64?: string, 
    includeWelcomeImage?: boolean,
    pageMargins?: { top: string; right: string; bottom: string; left: string }
  ): Promise<void> {
    try {
      // ✅ CRITICAL: ALWAYS apply backend base64 if provided (source of truth)
      // Note: With our fixes, yxml should always be empty here (provider disconnected during init)
      // This is defensive programming - if somehow yxml has content, we'll see it in logs
      if (initialBase64 && typeof initialBase64 === 'string') {
        const yxmlLengthBefore = yxml.length;
        
        // ✅ DEFENSIVE: Log if yxml unexpectedly has content (should never happen with our fixes)
        if (yxmlLengthBefore > 0) {
          console.error(`🚨 [UNEXPECTED-STATE] ${docId.slice(-8)} - yxml has ${yxmlLengthBefore} elements BEFORE backend apply!`);
          console.error(`🚨 [UNEXPECTED-STATE] This should not happen - provider is disconnected during init`);
          console.error(`🚨 [UNEXPECTED-STATE] Clearing and applying backend (source of truth)...`);
          
          // Clear unexpected content before applying backend
          doc.transact(() => {
            yxml.delete(0, yxmlLengthBefore);
          }, 'backend-override');
        }
        
        log.log('[initializeDocumentContent] Applying backend Y snapshot (base64) - SOURCE OF TRUTH', {
          base64Length: initialBase64.length,
          base64Preview: initialBase64.substring(0, 50) + '...',
          yxmlLengthBefore,
          unexpectedContent: yxmlLengthBefore > 0
        });
        
        try {
          const bin = atob(initialBase64);
          const update = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) {
            update[i] = bin.charCodeAt(i);
          }
          
          log.log(formatJSON({
            docId: docId.slice(-8),
            base64Length: initialBase64.length,
            binaryLength: bin.length,
            updateArrayLength: update.length,
            yxmlLengthBefore: yxmlLengthBefore,
            base64Preview: initialBase64
          }, `🔍 [BASE64_DECODE:${docId.slice(-8)}] About to apply Y.js update`, { maxStringLength: 200 }));
          
          Y.applyUpdateV2(doc, update);
          
          // Inspect Y.XmlFragment structure in detail
          const yxmlChildren = Array.from({ length: Math.min(yxml.length, 10) }, (_, i) => {
            const el = yxml.get(i);
            if (el instanceof Y.XmlElement) {
              const firstChild = el.length > 0 ? el.get(0) : null;
              return {
                index: i,
                type: el.nodeName,
                attributes: el.getAttributes(),
                childCount: el.length,
                textContent: el.toString(),
                firstChild: firstChild ? {
                  type: firstChild instanceof Y.XmlElement ? (firstChild as Y.XmlElement).nodeName : 'YXmlText',
                  content: firstChild.toString()
                } : null
              };
            } else {
              return {
                index: i,
                type: 'YXmlText',
                textContent: el.toString()
              };
            }
          });
          
          log.log(formatJSON({
            docId: docId.slice(-8),
            yxmlLengthAfter: yxml.length,
            yxmlChildren,
            docClientId: doc.clientID,
            docGuid: doc.guid
          }, `🔍 [BASE64_DECODE:${docId.slice(-8)}] Y.js update applied - FULL STRUCTURE`, { maxObjectDepth: 5 }));
          
          log.log('[initializeDocumentContent] ✅ Backend Y.js base64 applied successfully (SOURCE OF TRUTH)', {
            yxmlLengthAfter: yxml.length,
            elementsLoaded: yxml.length,
            hadUnexpectedContent: yxmlLengthBefore > 0
          });
          
          // Success - document loaded from backend (source of truth)
          if (yxml.length > 0) {
            this.expectedDocumentSizes.set(docId, yxml.length);
            return;
          }
        } catch (base64Error) {
          // Backend base64 is corrupted - mark document and let user know
          console.error(`🚨 [CORRUPTION] ${docId.slice(-8)} - Backend base64 corrupted:`, base64Error);
          this.corruptedDocuments.add(docId);
          log.warn('[initializeDocumentContent] Document marked as corrupted', { docId });
        }
      } else {
        // No base64 provided - this is a new document
        log.log('[initializeDocumentContent] No backend base64 (new document)', {
          hasBase64: !!initialBase64,
          yxmlLength: yxml.length
        });
      }
      
      // ✅ FIX: If we reach here without base64, this is a new/empty document
      if (!initialBase64 && initialContent) {
        // 🛡️ GUARD: Check if document already has content to prevent double insertion
        if (yxml.length > 0) {
          log.log('[initializeDocumentContent] ⚠️ Document already has content, skipping re-initialization', {
            docId: docId.slice(-8),
            existingNodes: yxml.length,
            reason: 'duplicate_initialization_prevented'
          });
          return;
        }
        
        log.log('[initializeDocumentContent] No Y.js binary available, treating as new document with initial content');
        
        try {
          // 🆕 NEW: Check if content is ProseMirror JSON (from Google Docs import or other sources)
          const isProseMirrorJson = typeof initialContent === 'object' && 
                                   initialContent !== null && 
                                   'type' in initialContent && 
                                   initialContent.type === 'doc';
          
          if (isProseMirrorJson) {
            log.log('[initializeDocumentContent] Detected ProseMirror JSON format', {
              hasContent: !!(initialContent as any).content,
              nodeCount: (initialContent as any).content?.length || 0
            });
            
            // Import ProseMirror utilities
            const { schema } = await import('../../editor/schema');
            const { Node } = await import('prosemirror-model');
            
            // Convert JSON to ProseMirror node
            const pmDoc = Node.fromJSON(schema, initialContent);
            
            log.log('[initializeDocumentContent] Converted ProseMirror JSON to node:', {
              nodeCount: pmDoc.childCount,
              docSize: pmDoc.nodeSize
            });
            
            // Convert ProseMirror doc to Y.js manually by creating Y.XmlElements
            // This is more reliable than using internal y-prosemirror functions
            doc.transact(() => {
              // Helper function to recursively convert ProseMirror nodes to Y.js
              const convertNodeToYXml = (pmNode: any): any => {
                if (pmNode.isText) {
                  return new Y.XmlText(pmNode.text);
                }
                
                const yElement = new Y.XmlElement(pmNode.type.name);
                
                // Set attributes
                if (pmNode.attrs) {
                  for (const [key, value] of Object.entries(pmNode.attrs)) {
                    if (value !== null && value !== undefined) {
                      yElement.setAttribute(key, String(value));
                    }
                  }
                }
                
                // Set marks as attributes
                if (pmNode.marks && pmNode.marks.length > 0) {
                  for (const mark of pmNode.marks) {
                    yElement.setAttribute(`mark_${mark.type.name}`, JSON.stringify(mark.attrs || {}));
                  }
                }
                
                // Recursively add children
                if (pmNode.content) {
                  pmNode.content.forEach((child: any) => {
                    const yChild = convertNodeToYXml(child);
                    yElement.insert(yElement.length, [yChild]);
                  });
                }
                
                return yElement;
              };
              
              // Convert all top-level nodes and insert into yxml
              pmDoc.content.forEach((childNode: any) => {
                const yChild = convertNodeToYXml(childNode);
                yxml.insert(yxml.length, [yChild]);
              });
            }, 'google-docs-import');
            
            log.log('[initializeDocumentContent] ✅ Initialized document from ProseMirror JSON', {
              yxmlLength: yxml.length,
              pmDocSize: pmDoc.nodeSize,
              pmContentChildCount: pmDoc.content.childCount
            });
            
            if (includeWelcomeImage) {
              this.addImageToDocument(yxml, WELCOME_IMAGE_URL, docId);
            }
            
            return;
          }
          
          // Handle string content (HTML or Markdown)
          if (typeof initialContent !== 'string') {
            log.error('[initializeDocumentContent] Invalid content type:', typeof initialContent);
            throw new Error(`Expected string or ProseMirror JSON, got ${typeof initialContent}`);
          }
          
          const looksLikeHtml = /<\w+[^>]*>/.test(initialContent) || initialContent.trim().startsWith('<');
          if (looksLikeHtml) {
            // Use same path as paste: HTML → PM → y-prosemirror → Y.js
            // This ensures colors, alignment, and all formatting preserved correctly
            const htmlConvertStart = Date.now();
            log.log('[initializeDocumentContent] ⏱️ Starting HTML→PM→Y.js conversion (same as paste):', {
              htmlLength: initialContent.length,
              htmlPreview: initialContent.substring(0, 100)
            });
            
            // Parse HTML to ProseMirror (already normalized, no double-normalization)
            const container = document.createElement('div');
            container.innerHTML = initialContent;
            const pmDoc = PMDOMParser.fromSchema(schema).parse(container);
            
            log.log('[initializeDocumentContent] ⏱️ ProseMirror doc created:', {
              nodeCount: pmDoc.childCount,
              docSize: pmDoc.nodeSize
            });
            
            // Use y-prosemirror's conversion (same as paste/editor insert)
            const { prosemirrorToYXmlFragment } = await import('y-prosemirror');
            
            const transactStart = Date.now();
            doc.transact(() => {
              // Clear yxml if it has content (shouldn't, but defensive)
              if (yxml.length > 0) {
                yxml.delete(0, yxml.length);
              }
              
              // Use y-prosemirror's converter (battle-tested, handles all marks/attrs)
              prosemirrorToYXmlFragment(pmDoc, yxml);
            }, 'html-initialization');
            
            const totalDuration = Date.now() - htmlConvertStart;
            log.log('[initializeDocumentContent] ✅ Initialized document via y-prosemirror (same as paste):', {
              duration: totalDuration,
              yxmlLength: yxml.length,
              pmNodeCount: pmDoc.childCount
            });
          } else {
            // Handle markdown content for new documents
            const seed = createYElement('paragraph', { logPrefix: '[docsCRDT-NewDocMD]', docId });
            yxml.insert(0, [seed]);
            
            const ctx: any = { doc, yxml, docId };
            docReplaceWithMarkdown(ctx, { all: true }, initialContent, {
              suggestionMode: false,
              batchDescription: 'new document markdown initialization'
            } as any);
            log.log('[initializeDocumentContent] ✅ Initialized new document with markdown');
          }
          
          if (includeWelcomeImage) {
            this.addImageToDocument(yxml, WELCOME_IMAGE_URL, docId);
          }
        } catch (error) {
          log.error('[initializeDocumentContent] Error initializing new document:', error);
          throw error;
        }
      } else if (!initialBase64 && !initialContent) {
        log.log('[initializeDocumentContent] Creating empty document with default styling');
        // Create a minimal empty document - rely on doc_style defaults for font
        // Don't add font marks here, let the document default font apply
        const emptyParagraph = createYElement('paragraph', { 
          logPrefix: '[docsCRDT-Empty]', 
          docId
        });
        yxml.insert(0, [emptyParagraph]);
      }
      
      // Apply page margins if provided (from Word import)
      if (pageMargins) {
        const styleMap = doc.getMap('doc_style');
        doc.transact(() => {
          styleMap.set('pageMarginTop', pageMargins.top);
          styleMap.set('pageMarginRight', pageMargins.right);
          styleMap.set('pageMarginBottom', pageMargins.bottom);
          styleMap.set('pageMarginLeft', pageMargins.left);
        }, 'page-margins-init');
        
        log.log('✅ [PAGE_MARGINS] Applied from Word import:', pageMargins);
      } else {
        log.log('⚠️ [PAGE_MARGINS] No page margins provided (using defaults)');
      }
      
      log.log('[initializeDocumentContent] ✅ Document initialization completed for:', docId);
    } catch (error) {
      log.error('[initializeDocumentContent] ❌ CRITICAL: Document initialization failed:', error);
      throw error; // Re-throw to fail loudly instead of silently creating empty docs
    }
  }

  // Debug utility to test multiplayer functionality
  public debugMultiplayer(docId: string) {
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const provider = this.crdt.getProvider(docKey);
    
    if (!provider) {
      log.error('[DEBUG] No provider found for doc:', docId);
      return null;
    }
    
    const awarenessStates = provider.awareness.getStates();
    const debug = {
      docId: docId.slice(-8),
      roomName: provider.roomname,
      websocketUrl: provider.url,
      connected: provider.wsconnected,
      synced: provider.synced,
      awarenessStatesCount: awarenessStates.size,
      localState: provider.awareness.getLocalState(),
      allStates: Array.from(awarenessStates.entries()).map(([clientId, state]) => ({
        clientId,
        user: state.user,
        cursor: state.cursor
      }))
    };
    
    log.log('[DEBUG] Multiplayer state for doc:', debug);
    log.log('🔍 Multiplayer Debug:', debug);
    
    return debug;
  }

  /**
   * Helper method to add an image to a Y.js document
   */
  private addImageToDocument(yxml: Y.XmlFragment, imageUrl: string, docId?: string): void {
    try {
      log.log('[docsCRDT] Adding welcome image to document');
      
      // Create the image element as a block node (not inside a paragraph)
      const imageElement = new Y.XmlElement('image');
      imageElement.setAttribute('src', imageUrl);
      imageElement.setAttribute('alt', 'Beautiful river landscape - Welcome to River');
      imageElement.setAttribute('title', 'River - Your AI-powered document editor');
      imageElement.setAttribute('width', '700'); // Set width, let height scale naturally to maintain aspect ratio
      imageElement.setAttribute('alignment', 'center'); // Center the welcome image
      imageElement.setAttribute('borderRadius', '0'); // No border radius by default
      imageElement.setAttribute('shadow', 'none'); // No shadow by default
      
      // Add the image directly as a block element
      yxml.insert(yxml.length, [imageElement]);
      
      log.log('[docsCRDT] Successfully added welcome image to document');
    } catch (error) {
      log.error('[docsCRDT] Failed to add welcome image:', error);
      // Don't throw - we don't want image insertion failure to break document creation
    }
  }

  /**
   * 🛡️ CRITICAL SAVE: Validates and saves document with data loss prevention
   * Returns the base64 string if successful, null if validation fails
   */
  private async performValidatedSave(docId: string, yxml: Y.XmlFragment, options?: { 
    isRetry?: boolean;  // 🛡️ Track if this is a retry attempt
    isVersionRetry?: boolean;  // 🔥 Track if this is specifically a version conflict retry
  }): Promise<string | null> {
    const startTime = Date.now();
    
    // ✅ Declare variables outside try block so they're accessible in catch
    const elementCount = yxml.length;
    let base64 = '';
    
    try {
      // 🛡️ CRITICAL: Block empty document saves to prevent data loss
      
      if (elementCount === 0) {
        console.error(`🚨 [SAVE-BLOCKED] ${docId.slice(-8)} - REFUSING to save empty document`);
        
        // 🛡️ ADDITIONAL CHECK: Verify backend doesn't have content we're about to overwrite
        try {
          const backendEntity = await entityService.get(docId);
          if (backendEntity?.skeleton?.base64 && backendEntity.skeleton.base64.length > 100) {
            console.error(`🚨🚨🚨 [DATA-LOSS-PREVENTION] ${docId.slice(-8)} - Backend has ${backendEntity.skeleton.base64.length} bytes but trying to save empty!`);
            console.error(`🚨🚨🚨 [DATA-LOSS-PREVENTION] This would cause PERMANENT DATA LOSS - save absolutely blocked!`);
            // Absolutely block this save
          }
        } catch (e) {
          // If backend check fails, still block empty save
          console.warn(`⚠️ [SAVE-BLOCKED] Backend verification failed, but still blocking empty save`, e);
        }
        
        return null; // BLOCK the save
      }
      
      // 🛡️ CORRUPTION CHECK: Block saves for documents with corrupted base64
      if (this.corruptedDocuments.has(docId)) {
        console.error(`🚨🚨🚨 [SAVE-BLOCKED-CORRUPTION] ${docId.slice(-8)} - Document has corrupted base64 in backend`);
        console.error(`🚨🚨🚨 [SAVE-BLOCKED-CORRUPTION] Blocking ALL saves to prevent overwriting potentially recoverable data`);
        console.error(`🚨🚨🚨 [MANUAL-RECOVERY-REQUIRED] This document needs manual recovery by administrator`);
        
        // TODO: Show user-facing error toast or modal
        // For now, just block silently and log loudly
        return null;
      }

      log.log('[performValidatedSave] Saving', { docId: docId.slice(-8), elements: elementCount });

      // Skip all HTML conversion - only save Y.js binary with V2 consistency
      if (yxml.doc) {
        const update = Y.encodeStateAsUpdateV2(yxml.doc);
        
        // ✅ OPTIMIZED: Use array.join() instead of string concatenation for better performance
        const uint8Array = new Uint8Array(update);
        const binaryChunks: string[] = [];
        const chunkSize = 8192; // Process in 8KB chunks to avoid stack overflow
        
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          const chunk = uint8Array.slice(i, i + chunkSize);
          binaryChunks.push(String.fromCharCode(...chunk));
        }
        const binaryString = binaryChunks.join('');
        
        base64 = btoa(binaryString);
      }
      
      // ✅ NEW: Compute metadata (nodeCount, wordCount) from Y.js document
      const nodeCount = yxml.length;
      // ✅ CRITICAL FIX: Skip word count to avoid infinite loop
      // getTextContent() calls initProseMirrorDoc() which triggers Y.js transactions
      // This causes observer → save → getTextContent() → infinite loop!
      // Word count is nice-to-have, not critical for saves
      const wordCount = 0;  // Skip word count computation to prevent infinite loops
      
      // ✅ ARCHITECTURE: Direct backend save for document content (not entity metadata)
      // Document content (base64) is stripped from entityStore cache, so it should NOT
      // flow through the entity mutation funnel. This is a CRDT layer saving to backend.
      
      // ✅ FIX #2: Save to backend FIRST, update cache ONLY after success
      // This prevents phantom "saved" state when backend save fails
      
      log.log(`📝 [SAVE-DEBUG] ${docId.slice(-8)} - Attempting save:`, {
        elementCount,
        base64Length: base64.length,
        isRetry: !!options?.isRetry
      });
      
      const skeletonPayload: Record<string, unknown> = {
        '@type': 'Doc',
        metadata: {
          nodeCount,
          wordCount,
          lastSaved: new Date().toISOString()
        }
      };
      if (!SERVER_IS_DURABILITY_BOUNDARY) {
        skeletonPayload.base64 = base64;
      }
      await entityService.mergeUpdate(docId, {
        skeleton: skeletonPayload
      });
      
      log.log(`✅ [SAVE-DEBUG] ${docId.slice(-8)} - Save succeeded!`);
      
      // ✅ Backend save succeeded! Now safe to update local state
      
      // Note: Version tracking temporarily disabled due to corrupted versions
      // Will re-enable after version reset
      
      const duration = Date.now() - startTime;
      
      log.log('[performValidatedSave] ✅ Backend save successful', {
        docId: docId.slice(-8),
        base64Length: base64.length,
        elements: elementCount,
        duration
      });
      
      this.expectedDocumentSizes.set(docId, elementCount);
      
      // 🔥 CRITICAL FIX: Invalidate request cache after save to ensure next fetch gets fresh data
      // Without this, page refresh might load stale cached response from before the save
      const { requestCacheService } = await import('../../../../services/requestCacheService');
      const { resolveToActualId } = await import('../../../../utils/llmIdMapper');
      const actualId = resolveToActualId(docId);
      requestCacheService.invalidate(`entity-${actualId}`);
      log.log('[performValidatedSave] Invalidated request cache', { docId: docId.slice(-8), cacheKey: `entity-${actualId}` });
      
      // 🔥 CRITICAL FIX: Update EntityStore cache with fresh metadata
      // ⚠️ IMPORTANT: This block only runs if backend save succeeded (no error thrown above)
      // Without this, AI context builder reads stale metadata from cache
      try {
        log.log('[METADATA_CACHE_UPDATE] ========== Starting cache update ==========');
        log.log('[METADATA_CACHE_UPDATE] Document info:', {
          docId: docId.slice(-8),
          actualId: actualId.slice(-8),
          nodeCount,
          wordCount
        });
        
        const { default: useEntityStore } = await import('../../../../stores/entityStore');
        const entityStore = useEntityStore.getState();
        
        log.log('[METADATA_CACHE_UPDATE] EntityStore loaded:', {
          totalRootEntities: entityStore.entities.length
        });
        
        // Flatten hierarchy to search all entities (not just root level)
        const flattenEntities = (entities: any[]): any[] => {
          const result: any[] = [];
          const traverse = (entity: any) => {
            const { children, ...entityWithoutChildren } = entity;
            result.push(entityWithoutChildren);
            if (children && Array.isArray(children)) {
              children.forEach(traverse);
            }
          };
          entities.forEach(traverse);
          return result;
        };
        
        const flatEntities = flattenEntities(entityStore.entities);
        log.log('[METADATA_CACHE_UPDATE] Flattened hierarchy:', {
          hierarchicalCount: entityStore.entities.length,
          flatCount: flatEntities.length
        });
        
        const cachedEntity = flatEntities.find(e => 
          e._id === actualId || e._id === docId
        );
        
        if (cachedEntity) {
          log.log('[METADATA_CACHE_UPDATE] ✅ Entity found in cache:', {
            entityId: cachedEntity._id.slice(-8),
            entityName: cachedEntity.name,
            entityType: cachedEntity.entityType,
            hasSkeleton: !!cachedEntity.skeleton,
            oldMetadata: cachedEntity.skeleton?.metadata
          });
          
          // 🔍 Create timestamp ONCE to ensure consistency across update and logs
          const updateTimestamp = new Date().toISOString();
          const newMetadata = {
            nodeCount,
            wordCount,
            lastSaved: updateTimestamp
          };
          
          const updatedEntity = {
            ...cachedEntity,
            skeleton: {
              ...(cachedEntity.skeleton || {}),  // ✅ Safe spread - handles undefined skeleton
              metadata: newMetadata
            },
            updated_at: updateTimestamp
          };
          
          // 🔍 DEBUG: Log what we're about to send to _applyLocalUpsert
          log.log('[METADATA_CACHE_UPDATE] 🔧 Calling _applyLocalUpsert with:', {
            entityId: updatedEntity._id.slice(-8),
            hasMetadata: !!updatedEntity.skeleton?.metadata,
            metadata: updatedEntity.skeleton?.metadata
          });
          
          entityStore._applyLocalUpsert(updatedEntity, {
            log: false,  // Silent update (performance save)
            source: 'local',
            syncCRDT: false  // Don't sync back (already saved to backend)
          });
          
          log.log('[METADATA_CACHE_UPDATE] ✅ Cache update attempted:', {
            docId: docId.slice(-8),
            newMetadata
          });
          
          // 🔍 CRITICAL: Get FRESH EntityStore state after _applyLocalUpsert
          // This ensures we're reading the CURRENT state, not a stale reference
          const freshEntityStore = useEntityStore.getState();
          const updatedFlatEntities = flattenEntities(freshEntityStore.entities);
          const verifyEntity = updatedFlatEntities.find(e => 
            e._id === actualId || e._id === docId
          );
          
          const verifiedMetadata = verifyEntity?.skeleton?.metadata;
          const matches = verifiedMetadata?.nodeCount === nodeCount && 
                         verifiedMetadata?.wordCount === wordCount;
          
          log.log('[METADATA_CACHE_UPDATE] ✅ Verification check:', {
            expected: newMetadata,  // ✅ Show full expected metadata including timestamp
            actual: verifiedMetadata,
            matches,
            matchDetails: {
              nodeCountMatch: verifiedMetadata?.nodeCount === nodeCount,
              wordCountMatch: verifiedMetadata?.wordCount === wordCount,
              hasLastSaved: !!verifiedMetadata?.lastSaved
            }
          });
          
          // 🚨 If verification failed, log detailed debugging
          if (!matches) {
            console.error('[METADATA_CACHE_UPDATE] ❌ VERIFICATION FAILED - Update did NOT persist!', {
              expected: newMetadata,
              actual: verifiedMetadata,
              oldMetadata: cachedEntity.skeleton?.metadata,
              entityId: verifyEntity?._id?.slice(-8),
              hasSkeleton: !!verifyEntity?.skeleton,
              hasMetadata: !!verifiedMetadata,
              // 🔍 Deep dive into what changed
              comparison: {
                nodeCount: {
                  old: cachedEntity.skeleton?.metadata?.nodeCount,
                  expected: nodeCount,
                  actual: verifiedMetadata?.nodeCount
                },
                wordCount: {
                  old: cachedEntity.skeleton?.metadata?.wordCount,
                  expected: wordCount,
                  actual: verifiedMetadata?.wordCount
                }
              },
              possibleCauses: [
                'Race condition: HierarchyStore overwrote our changes during rebuild',
                'Zustand state not fully committed',
                'normalizeEntityForCache stripped metadata',
                'Entity reference changed during _applyLocalUpsert'
              ]
            });
          }
          
          log.log('[performValidatedSave] ✅ Updated EntityStore cache with fresh metadata', {
            docId: docId.slice(-8),
            nodeCount,
            wordCount
          });
        } else {
          console.warn('[METADATA_CACHE_UPDATE] ⚠️ Entity not found in cache:', {
            docId: docId.slice(-8),
            actualId: actualId.slice(-8),
            searchedInTotal: flatEntities.length,
            availableEntityIds: flatEntities.slice(0, 10).map(e => ({
              id: e._id?.slice(-8),
              name: e.name,
              type: e.entityType
            }))
          });
          
          log.warn('[performValidatedSave] Entity not found in cache:', {
            docId: docId.slice(-8)
          });
        }
        
        log.log('[METADATA_CACHE_UPDATE] ========== Cache update complete ==========');
      } catch (error) {
        console.error('[METADATA_CACHE_UPDATE] ❌ Failed to update EntityStore cache:', {
          error: error instanceof Error ? error.message : error,
          stack: error instanceof Error ? error.stack : undefined,
          docId: docId.slice(-8)
        });
        
        log.warn('[performValidatedSave] Failed to update EntityStore cache:', error);
        // Non-critical: EntityStore will eventually refresh from backend
      }
      
      // 🔥 CRITICAL FIX: Small delay to ensure backend write is fully committed before next read
      // MongoDB writes are eventually consistent - this ensures read-after-write consistency
      // Only wait if this is a content save (base64), not metadata-only updates
      if (base64.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 100)); // 100ms delay for write propagation
        log.log('[performValidatedSave] Write propagation delay completed', { docId: docId.slice(-8) });
      }
      
      // 🛡️ LAYER 3: Clear any previous unsaved warning on successful save
      if (document.title.startsWith('⚠️ UNSAVED - ')) {
        document.title = document.title.replace('⚠️ UNSAVED - ', '');
        console.warn(`✅ [SAVE-SUCCESS] Cleared unsaved warning from tab title`);
      }
      
      // 🔄 SYNC CHECK: Verify WebSocket is connected for real-time sync
      // The save to EntityService is complete, but WebSocket sync happens separately
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const provider = this.crdt.getProvider(docKey);
      if (provider && !provider.wsconnected) {
        console.warn(`⚠️ [SYNC-WARNING] ${docId.slice(-8)} - EntityService save complete but WebSocket disconnected`);
        console.warn(`⚠️ [SYNC-WARNING] Content saved to backend but may not sync to other clients until reconnect`);
      } else if (!provider) {
        console.warn(`⚠️ [SYNC-WARNING] ${docId.slice(-8)} - No WebSocket provider found`);
      }

      // ✅ CRITICAL FIX: Skip summary update to avoid infinite loop
      // getTextContent() calls initProseMirrorDoc() which triggers Y.js transactions
      // This causes observer → save → getTextContent() → infinite loop!
      // Summary updates are nice-to-have, not critical for saves
      // After successful save, schedule summary update
      // try {
      //   const textContent = this.getTextContent(yxml);
      //   summaryService.scheduleSummaryUpdate(docId, textContent);
      // } catch (summaryError) {
      //   log.warn(`[docsCRDT] Failed to schedule summary update for doc ${docId}:`, summaryError);
      // }
      
      return base64;
      
    } catch (error) {
      const duration = Date.now() - startTime;
      
      // ✅ FIX #7: Enhanced error logging with detailed context
      const errorContext = {
        docId: docId.slice(-8),
        duration,
        elementCount,
        base64Length: base64?.length || 0,
        errorType: error?.constructor?.name || 'Unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStatus: (error as any)?.status,
        // Network diagnostics
        isOnline: navigator.onLine,
        connectionType: (navigator as any)?.connection?.effectiveType,
        // Error categorization
        isNetworkError: !navigator.onLine || 
                       (error instanceof Error && (
                         error.message.includes('network') || 
                         error.message.includes('fetch') ||
                         error.message.includes('Failed to fetch')
                       )),
        isAuthError: (error as any)?.status === 401 || (error as any)?.status === 403,
        isServerError: (error as any)?.status >= 500,
        isClientError: (error as any)?.status >= 400 && (error as any)?.status < 500
      };
      
      console.error(`❌ [ENTITY-SAVE-FAIL] ${docId.slice(-8)} - EntityService save FAILED`, errorContext);
      console.error(`⚠️⚠️⚠️ [SAVE-FAILED] DO NOT CLOSE THIS TAB - YOUR CHANGES ARE NOT SAVED! ⚠️⚠️⚠️`);
      console.error(`⚠️⚠️⚠️ [SAVE-FAILED] Keep this tab open until you see save confirmation`);
      
      // Note: Version conflict handling removed (version checking disabled)
      // Will re-enable after version corruption is fixed
      
      // ✅ User-friendly error messages with specific guidance
      let userMessage = 'Failed to save document';
      let userDetails = '';
      let canRetry = true;
      
      if (!navigator.onLine) {
        userMessage = 'No internet connection';
        userDetails = 'Your changes will be saved when you reconnect.';
        canRetry = true;
      } else if (errorContext.isAuthError) {
        if ((error as any)?.status === 401) {
          userMessage = 'Session expired';
          userDetails = 'Please refresh the page and log in again.';
          canRetry = false;
        } else if ((error as any)?.status === 403) {
          userMessage = 'Permission denied';
          userDetails = 'You no longer have permission to edit this document.';
          canRetry = false;
        }
      } else if (errorContext.isServerError) {
        userMessage = 'Server error';
        userDetails = 'The server encountered an error. Please try again.';
        canRetry = true;
      } else if (errorContext.isNetworkError) {
        userMessage = 'Network error';
        userDetails = 'Please check your internet connection and try again.';
        canRetry = true;
      }
      
      // Show toast notification (will be implemented when toast system is available)
      // For now, log what the toast would say
      console.error(`[USER-NOTIFICATION] ${userMessage}${userDetails ? ': ' + userDetails : ''}`, {
        canRetry,
        retryAvailable: canRetry && !options?.isRetry
      });
      
      // 🛡️ LAYER 3: Change browser tab title to warn user
      if (!document.title.startsWith('⚠️ UNSAVED')) {
        document.title = '⚠️ UNSAVED - ' + document.title;
        console.error(`⚠️ [SAVE-FAILED] Tab title updated to warn user - visible in browser tab`);
      }
      
      // 🛡️ LAYER 3: Retry once after 5 seconds (catches transient network errors)
      if (!options?.isRetry) {
        console.warn(`🔄 [SAVE-RETRY] Will retry save in 5 seconds (general retry, not version conflict)...`);
        setTimeout(() => {
          console.warn(`🔄 [SAVE-RETRY] Executing delayed retry for ${docId.slice(-8)}...`);
          this.performValidatedSave(docId, yxml, { ...options, isRetry: true })
            .then((result) => {
              if (result) {
                console.warn(`✅ [SAVE-RETRY] Retry succeeded! Document saved.`);
              } else {
                console.error(`🚨 [SAVE-RETRY] Retry also failed - user intervention needed`);
              }
            })
            .catch((retryError) => {
              console.error(`🚨 [SAVE-RETRY] Retry threw error:`, retryError);
            });
        }, 5000);
      } else if (options?.isRetry && !options?.isVersionRetry) {
        console.error(`🚨 [SAVE-FAILED] General retry failed for ${docId.slice(-8)} - both attempts failed`);
        console.error(`🚨 [SAVE-FAILED] User must keep tab open. Y.js will sync via WebSocket.`);
      }
      
      log.error(`[docsCRDT] Failed to save content to DB for doc ${docId}:`, error);
      return null;
    }
  }

  // Get or create a per-document debounced save function
  private getDocumentDebouncedSave(docId: string): ReturnType<typeof debounce> {
    let debouncedFn = this.perDocumentDebouncedSaves.get(docId);
    
    if (!debouncedFn) {
      debouncedFn = debounce(async (yxml: Y.XmlFragment) => {
        try {
          await this.performValidatedSave(docId, yxml);
        } catch (error) {
          console.error(`[docsCRDT] Save failed for ${docId.slice(-8)}:`, error);
        }
      }, 500, {
        maxWait: 5000
      });
      
      this.perDocumentDebouncedSaves.set(docId, debouncedFn);
    }
    
    return debouncedFn;
  }
  
  private triggerDebouncedSave(docId: string, yxml: Y.XmlFragment): void {
    const debouncedFn = this.getDocumentDebouncedSave(docId);
    debouncedFn(yxml);
  }
  
  public flushDocumentSave(docId: string): void {
    const debouncedFn = this.perDocumentDebouncedSaves.get(docId);
    if (debouncedFn) {
      debouncedFn.flush();
    }
  }
  
  /**
   * ✅ FIX #6: Flush all pending saves for all documents
   * Used before critical state transitions (dataspace switch, app close)
   * Returns promise that resolves when all flushes complete
   */
  public async flushAllPendingSaves(): Promise<void> {
    log.log('[flushAllPendingSaves] Flushing all pending document saves', {
      documentCount: this.perDocumentDebouncedSaves.size
    });
    
    if (this.perDocumentDebouncedSaves.size === 0) {
      log.log('[flushAllPendingSaves] No pending saves to flush');
      return;
    }
    
    // Flush all debounced saves
    const flushPromises: Promise<void>[] = [];
    
    this.perDocumentDebouncedSaves.forEach((debouncedFn, docId) => {
      log.log('[flushAllPendingSaves] Flushing save for:', docId.slice(-8));
      
      // Flush returns the debounced function's return value (Promise<void>)
      const flushPromise = debouncedFn.flush();
      if (flushPromise && typeof flushPromise.then === 'function') {
        flushPromises.push(flushPromise);
      }
    });
    
    // Wait for all flushes to complete (with timeout)
    try {
      await Promise.race([
        Promise.all(flushPromises),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Flush timeout')), 5000)
        )
      ]);
      
      log.log('[flushAllPendingSaves] ✅ All saves flushed successfully');
    } catch (error) {
      if (error instanceof Error && error.message === 'Flush timeout') {
        log.error('[flushAllPendingSaves] ⚠️ Flush timeout after 5 seconds', {
          pendingCount: flushPromises.length
        });
      } else {
        log.error('[flushAllPendingSaves] ❌ Flush failed:', error);
      }
      throw error;
    }
  }
  
  // Force save document directly - bypasses debounce for guaranteed persistence
  public async forceSaveDocument(docId: string): Promise<boolean> {
    try {
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const doc = this.crdt.getYDoc(docKey);
      
      if (!doc) {
        console.error(`[docsCRDT] forceSaveDocument: No Y.Doc found for ${docId.slice(-8)}`);
        return false;
      }
      
      const yxml = doc.getXmlFragment('prosemirror');
      
      if (!yxml || yxml.length === 0) {
        return false;
      }
      
      const result = await this.performValidatedSave(docId, yxml);
      return !!result;
    } catch (error) {
      console.error(`[docsCRDT] forceSaveDocument failed for ${docId.slice(-8)}:`, error);
      return false;
    }
  }

  // Helper method to extract text content from Y.js
  private   getTextContent(yxml: Y.XmlFragment): string {
    // ✅ CRITICAL FIX: Use fragment object as guard key (works across all contexts)
    // initProseMirrorDoc() can trigger Y.js transactions, which fire observer,
    // which triggers save, which calls getTextContent() again = INFINITE LOOP!
    const fragmentKey = yxml as any;  // Use object reference as unique key
    
    if (this.isInTextExtraction.has(fragmentKey)) {
      console.warn(`⚠️ [REENTRY-GUARD] Skipping nested getTextContent() call (infinite loop prevention)`);
      return '';  // Return empty to break the loop
    }
    
    this.isInTextExtraction.add(fragmentKey);
    
    try {
      // Convert to ProseMirror and extract text with error handling
      try {
        const { doc } = initProseMirrorDoc(yxml, schema);
        return doc.textContent;
      } catch (syncError) {
        log.error('[docsCRDT] CRITICAL: Failed to extract text content via ProseMirror!', syncError);
        
        // 🚨 CRITICAL: DO NOT USE toString() FALLBACK - causes contamination!
        // The toString() method returns XML like <fontFamily>text</fontFamily>
        // This contaminates summaries, text extraction, and any other text-based operations
        
        throw new Error(`Critical failure extracting text content from YJS document: ${syncError instanceof Error ? syncError.message : 'Unknown error'}. ProseMirror conversion is required for safe text extraction.`);
        
        // ❌ REMOVED: Dangerous toString() fallback
        // This was a major source of persistent contamination in the system
      }
    } catch (error) {
      log.warn('[docsCRDT] Failed to extract text content, returning empty string:', error);
      // Fallback to empty string if all else fails
      return '';
    } finally {
      // ✅ Always remove reentry guard when done
      this.isInTextExtraction.delete(fragmentKey);
    }
  }

  getDocName(docId: string): string | undefined {
    const entityType = this.entityTypes.get(docId);
    const docKey = this.getDocKey(docId, entityType);
    const doc = this.crdt.getYDoc(docKey);
    if (doc) {
      // 🚨 CRITICAL FIX: DO NOT use doc.getText('name').toString() - Y.Text could contain formatting!
      try {
        const nameText = doc.getText('name');
        const anyNameText = nameText as any;
        if (anyNameText._content && typeof anyNameText._content === 'string') {
          return anyNameText._content;
        } else {
          console.warn('[docsCRDTService] 🚨 Using dangerous toString() for document name extraction!');
          const contaminated = nameText.toString();
          return contaminated
            .replace(/<fontFamily[^>]*>.*?<\/fontFamily>/g, '')
            .replace(/<[^>]*>/g, '')
            .replace(/fontFamily/g, '');
        }
      } catch (error) {
        console.error('[docsCRDTService] CRITICAL: Cannot extract document name safely:', error);
        return '';
      }
    }
    return undefined;
  }


  /**
   * Save comments to CRDT for a document
   */
  async saveComments(docId: string, commentsData: string): Promise<void> {
    try {
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const doc = this.crdt.ensureInitialized(docKey);
      
      if (doc) {
        const commentsText = doc.getText('comments');
        commentsText.delete(0, commentsText.length);
        commentsText.insert(0, commentsData);
        
        log.log('[saveComments] Saved comments to CRDT for doc:', docId);
      }
    } catch (error) {
      log.error('[saveComments] Failed to save comments:', error);
      throw error;
    }
  }

  /**
   * Load comments from CRDT for a document
   */
  async loadComments(docId: string): Promise<string | null> {
    try {
      const entityType = this.entityTypes.get(docId);
      const docKey = this.getDocKey(docId, entityType);
      const doc = this.crdt.ensureInitialized(docKey);
      
      if (doc) {
        const commentsText = doc.getText('comments');
        // 🚨 CRITICAL FIX: DO NOT use commentsText.toString() - Y.Text could contain formatting contamination!
        let commentsData = '';
        try {
          const anyCommentsText = commentsText as any;
          if (anyCommentsText._content && typeof anyCommentsText._content === 'string') {
            commentsData = anyCommentsText._content;
          } else {
            console.warn('[docsCRDTService] 🚨 Using dangerous toString() for comments extraction!');
            const contaminated = commentsText.toString();
            commentsData = contaminated
              .replace(/<fontFamily[^>]*>.*?<\/fontFamily>/g, '')
              .replace(/<[^>]*>/g, '')
              .replace(/fontFamily/g, '');
          }
        } catch (error) {
          console.error('[docsCRDTService] CRITICAL: Cannot extract comments safely:', error);
          commentsData = '';
        }
        
        if (commentsData) {
          log.log('[loadComments] Loaded comments from CRDT for doc:', docId);
          return commentsData;
        }
      }
      
      return null;
    } catch (error) {
      log.error('[loadComments] Failed to load comments:', error);
      return null;
    }
  }

  /**
   * 🚀 Calculate document metadata without saving
   * Used to update EntityStore immediately after AI edits
   * 
   * @param docId The document ID to calculate metadata for
   * @returns Metadata object with nodeCount and wordCount, or null if document not found
   */
  public calculateDocumentMetadata(docId: string): { nodeCount: number; wordCount: number } | null {
    const yxml = this.yContentCache.get(docId);
    if (!yxml) {
      log.warn(`[calculateDocumentMetadata] No Y.js content found for ${docId}`);
      return null;
    }
    
    const nodeCount = yxml.length;
    let wordCount = 0;
    
    try {
      const textContent = this.getTextContent(yxml);
      wordCount = textContent.split(/\s+/).filter((w: string) => w.trim()).length;
    } catch (error) {
      log.warn('[calculateDocumentMetadata] Failed to compute word count:', error);
    }
    
    log.log('[calculateDocumentMetadata] Calculated metadata:', {
      docId: docId.slice(-8),
      nodeCount,
      wordCount
    });
    
    return { nodeCount, wordCount };
  }
}

export const docsCRDTService = new DocsCRDTService(crdtServiceWS);

  // Expose debug functions globally for testing
  if (typeof window !== 'undefined') {
    (window as any).debugDocMultiplayer = (docId: string) => docsCRDTService.debugMultiplayer(docId);
    
    // ✅ ROBUSTNESS: Health check function
    (window as any).checkMultiplayerHealth = () => {
      const docId = '68d4a05fa826cb5641d80113';
      const docKey = `doc:${docId}`;
      
      const provider = docsCRDTService.getProviderForTesting(docKey);
      const doc = docsCRDTService.getProviderForTesting(docKey)?.doc;
      
      const health = {
        docId: docId.slice(-8),
        hasProvider: !!provider,
        providerConnected: provider?.wsconnected || false,
        providerSynced: provider?.synced || false,
        wsReadyState: provider?.ws?.readyState,
        wsUrl: provider?.url,
        documentExists: !!doc,
        documentSize: doc?.getXmlFragment('prosemirror').length || 0,
        awarenessCount: provider?.awareness?.getStates?.()?.size || 0,
        globalLocks: Array.from(globalInitializationLock.keys()),
        timestamp: new Date().toISOString()
      };
      
      log.log('🏥 [MULTIPLAYER HEALTH CHECK]:', health);
      
      if (!provider || !provider.wsconnected) {
        console.warn('⚠️ [HEALTH] WebSocket connection issues detected');
      }
      
      if (health.awarenessCount < 2) {
        console.warn('⚠️ [HEALTH] Only', health.awarenessCount, 'users connected (expected 2+)');
      }
      
      return health;
    };
    (window as any).resetCorruptedDoc = () => {
      const docId = '68d4a05fa826cb5641d80113';
      
      if (docsCRDTService.resetDocument(docId)) {
        log.log('🔄 Reloading page to reinitialize document...');
        location.reload();
      }
    };
    
    // Quick test function
    (window as any).testMultiplayerQuick = () => {
      // Get the current document ID from URL (supports both /docs/ID and spaces with openEntity)
      let docId = null;
      
      // Try standalone docs URL pattern
      const standaloneMatch = window.location.pathname.match(/\/docs\/([a-f0-9]+)/);
      if (standaloneMatch) {
        docId = standaloneMatch[1];
      }
      
      // Try space URL with openEntity parameter
      if (!docId) {
        const openEntityMatch = window.location.search.match(/openEntity=([a-f0-9]+)/);
        if (openEntityMatch) {
          docId = openEntityMatch[1];
        }
      }
      
      // Try to get from active window state
      if (!docId) {
        try {
          // Access spaceStore from global window object
          const spaceStore = (window as any).__spaceStore || 
                           (window as any).useSpaceStore?.getState?.() ||
                           useSpaceStore.getState();
          
          if (spaceStore) {
            const state = typeof spaceStore === 'function' ? spaceStore() : spaceStore;
            const activeWindow = state.activeWindowId ? state.windows[state.activeWindowId] : null;
            
            if (activeWindow?.appType === 'docs') {
              docId = activeWindow.applicationState?.docs?.activeDocId || activeWindow.entityId;

              log.log('🔍 Found docId from space store:', {
                docId,
                activeWindowId: state.activeWindowId,
                windowAppType: activeWindow.appType
              });
            }
          }
        } catch (error) {
          console.warn('Could not get docId from space store:', error);
        }
      }
      
      if (docId) {
        log.log('🔍 Found document ID:', docId);
        return docsCRDTService.debugMultiplayer(docId);
      } else {
        log.log('❌ No document ID found. Make sure you have a document open.');
        log.log('💡 URL patterns supported:');
        log.log('  - /docs/DOCUMENT_ID');
        log.log('  - /spaces/SPACE_ID?openEntity=DOCUMENT_ID');
        log.log('  - /spaces/SPACE_ID (with active document window)');
        return null;
      }
    };
    
    // Debug image persistence
    (window as any).debugImagePersistence = () => {
      const urlMatch = window.location.pathname.match(/\/docs\/([a-f0-9]+)/);
      if (!urlMatch) {
        log.log('No document ID found in URL. Open a document first.');
        return null;
      }
      
      const docId = urlMatch[1];
      const provider = docsCRDTService.getProvider(docId);
      
      if (!provider) {
        log.log('No provider found for document');
        return null;
      }
      
      const doc = provider.doc;
      const yxml = doc.getXmlFragment('prosemirror');
      
      log.log('Y.js document structure:');
      for (let i = 0; i < yxml.length; i++) {
        const item = yxml.get(i);
        if (item instanceof Y.XmlElement) {
          log.log(`Item ${i}:`, {
            nodeName: item.nodeName,
            attributes: item.getAttributes(),
            textContent: item.toString().substring(0, 100)
          });
        } else {
          log.log(`Item ${i}:`, {
            type: 'YXmlText',
            textContent: item.toString().substring(0, 100)
          });
        }
      }
      
      return { yxmlLength: yxml.length };
    };
  
  // Test Y.js document sync (not just awareness)
  (window as any).testDocSync = () => {
    // Use the same logic as testMultiplayerQuick to find docId
    let docId = null;
    
    // Try standalone docs URL pattern
    const standaloneMatch = window.location.pathname.match(/\/docs\/([a-f0-9]+)/);
    if (standaloneMatch) {
      docId = standaloneMatch[1];
    }
    
    // Try space URL with openEntity parameter
    if (!docId) {
      const openEntityMatch = window.location.search.match(/openEntity=([a-f0-9]+)/);
      if (openEntityMatch) {
        docId = openEntityMatch[1];
      }
    }
    
    // Try to get from active window state
    if (!docId) {
      try {
        // Access spaceStore from global window object
        const spaceStore = (window as any).__spaceStore || 
                         (window as any).useSpaceStore?.getState?.() ||
                         useSpaceStore.getState();
        
        if (spaceStore) {
          const state = typeof spaceStore === 'function' ? spaceStore() : spaceStore;
          const activeWindow = state.activeWindowId ? state.windows[state.activeWindowId] : null;
          
          if (activeWindow?.appType === 'docs') {
            docId = activeWindow.applicationState?.docs?.activeDocId || activeWindow.entityId;
          }
        }
      } catch (error) {
        console.warn('Could not get docId from space store:', error);
      }
    }
    
    if (!docId) {
      log.log('❌ No document ID found. Make sure you have a document open.');
      return null;
    }
    
    log.log('🔍 Testing document sync for:', docId);
    const provider = docsCRDTService.getProvider(docId);
    
    if (!provider) {
      log.log('No provider found for document');
      return null;
    }
    
    // Test by adding a simple text update to Y.js
    const doc = provider.doc;
    if (doc) {
      const testText = doc.getText('multiplayer-test');
      const timestamp = new Date().toISOString();
      testText.insert(0, `Test from client at ${timestamp}\n`);
      
      log.log('Added test text to Y.js document. Check other client to see if it appears.');
      return {
        testAdded: true,
        timestamp,
        currentContent: testText.toString()
      };
    }
    
    return { error: 'No Y.Doc found' };
  };
} 